import React, { useState, useEffect, useRef, useMemo } from "react";
import { getDocs, doc, getDoc, collection, updateDoc, onSnapshot, query, where, runTransaction, collectionGroup } from "firebase/firestore";
import { setDoc } from "firebase/firestore";
import { db } from "../firebase";
import DateRangePopup from "../components/DateRangePopup";
import { useLocation } from 'react-router-dom';
import ReportsSection from "../components/ReportDropdown3"
import Chart from "../components/Chart";
import { motion, AnimatePresence } from "framer-motion";
import ChartMobile from "../components/ChartMobile";
import { useCountry } from './CountryContext';

// Init Firebase singletons

// Register chart.js components
const SMMEsOptions = [
  {
    "heading": "Smallholder Commercial Farms",
    "items": [
      {
        "label": "Small-scale farms operating for profit. Use improved inputs, some mechanization, and sell surplus to local markets. Often family-run but may hire seasonal labor. Example: A 5-hectare maize farm using hybrid seeds and selling to a local mill.",
        "value": "ESC"
      }
    ]
  },
  {
    "heading": "Medium-Scale Farms",
    "items": [
      {
        "label": "Larger landholding than smallholders (e.g., 10–100 hectares). Often mechanized and more capitalized. Supply supermarkets, processors, or export markets. Example: A 50-hectare rice farm with irrigation and a tractor, selling to a national rice brand.",
        "value": "EMS"
      }
    ]
  },
  {
    "heading": "Farmer Cooperatives / Producer Organizations",
    "items": [
      {
        "label": "Groups of farmers who pool resources to gain market power. May own equipment, storage, or transport collectively. Can register as SMEs and operate commercially. Example: A cooperative of 100 tomato farmers owning a cold storage unit.",
        "value": "EFC"
      }
    ]
  },
  {
    "heading": "Contract Farming SMEs",
    "items": [
      {
        "label": "Farmers or farm groups under formal agreements to supply crops/livestock to processors or exporters. Often receive inputs and training from buyers. Example: Poultry farmers supplying eggs to a food company under a contract.",
        "value": "ECF"
      }
    ]
  },
  {
    "heading": "Niche or Specialized Farms",
    "items": [
      {
        "label": "Farms focused on high-value or specialized production: Organic farms, Horticulture farms (vegetables, flowers, herbs), Aquaculture enterprises, Livestock or poultry SMEs, Beekeeping and honey production. Example: A small farm producing organic herbs for export.",
        "value": "ENS"
      }
    ]
  },
  {
    "heading": "Agri-Entrepreneurs with Integrated Operations",
    "items": [
      {
        "label": "These SMEs farm and also manage part of the value chain, such as: On-farm processing (e.g., dairy to yogurt), Direct-to-consumer sales (farm shops, online orders). Example: A mushroom farmer selling pre-packed mushrooms to urban grocers.",
        "value": "EAE"
      }
    ]
  },
  {
    "heading": "Youth or Women-led Farming Startups",
    "items": [
      {
        "label": "Often tech-savvy and innovative. Use climate-smart or digital farming tools. May focus on urban/peri-urban agriculture. Example: A woman-led hydroponic vegetable farm near a city.",
        "value": "EYW"
      }
    ]
  },
]

const getRegionMap = (country) => {
  const countryMappings = {
    'South Africa': {
      "Gauteng": "01",
      "Eastern Cape": "02",
      "Limpopo": "03",
      "KwaZulu-Natal": "04",
      "Mpumalanga": "05",
      "Western Cape": "06",
      "North West": "07",
      "Northern Cape": "08",
      "Free State": "09"
    },
    'Liberia': {
  "Bomi": "01",
  "Bong": "02",
  "Gbarpolu": "03",
  "Grand Bassa": "04",
  "Grand Cape Mount": "05",
  "Grand Gedeh": "06",
  "Grand Kru": "07",
  "Lofa": "08",
  "Margibi": "09",
  "Maryland": "10",
  "Montserrado": "11",
  "Nimba": "12",
  "River Cess": "13",
  "River Gee": "14",
  "Sinoe": "15"
},
    'Zimbabwe': {
  "Bulawayo": "01",
  "Harare": "02",
  "Manicaland": "03",
  "Mashonaland Central": "04",
  "Mashonaland East": "05",
  "Mashonaland West": "06",
  "Masvingo": "07",
  "Matabeleland North": "08",
  "Matabeleland South": "09",
  "Midlands": "10"
},
    'Botswana': {
      "Central": "27",
      "Chobe": "67",
      "Ghanzi": "65",
      "Kgalagadi": "69",
      "Kgatleng": "31",
      "Kweneng": "31",
      "North-East": "26",
      "North-West (Ngamiland)": "66",
      "South-East": "24",
      "Southern": "62"
    },
    'Kenya': {
      "Mombasa": "1",
      "Kwale": "2",
      "Kilifi": "3",
      "Tana River": "4",
      "Lamu": "5",
      "Taita Taveta": "6",
      "Garissa": "7",
      "Wajir": "8",
      "Mandera": "9",
      "Marsabit": "10",
      "Isiolo": "11",
      "Meru": "12",
      "Tharaka-Nithi": "13",
      "Embu": "14",
      "Kitui": "15",
      "Machakos": "16",
      "Makueni": "17",
      "Nyandarua": "18",
      "Nyeri": "19",
      "Kirinyaga": "20",
      "Murang'a": "21",
      "Kiambu": "22",
      "Turkana": "23",
      "West Pokot": "24",
      "Samburu": "25",
      "Trans Nzoia": "26",
      "Uasin Gishu": "27",
      "Elgeyo-Marakwet": "28",
      "Nandi": "29",
      "Baringo": "30",
      "Laikipia": "31",
      "Nakuru": "32",
      "Narok": "33",
      "Kajiado": "34",
      "Kericho": "35",
      "Bomet": "36",
      "Kakamega": "37",
      "Vihiga": "38",
      "Bungoma": "39",
      "Busia": "40",
      "Siaya": "41",
      "Kisumu": "42",
      "Homa Bay": "43",
      "Migori": "44",
      "Kisii": "45",
      "Nyamira": "46",
      "Nairobi City": "47"
    },
    'Namibia': {
      "Erongo": "08",
      "Hardap": "12",
      "Kavango East": "06",
      "Kavango West": "14",
      "Khomas": "11",
      "Kunene": "01",
      "Ohangwena": "04",
      "Omaheke": "10",
      "Omusati": "02",
      "Oshana": "03",
      "Oshikoto": "05",
      "Otjozondjupa": "09",
      "Zambezi": "07",
      "Karas": "013"
    },
    'Zambia': {
      "Western": "1",
      "Central": "2",
      "Eastern": "3",
      "Luapula": "4",
      "Northern": "5",
      "North-Western": "6",
      "Southern": "7",
      "Copperbelt": "8",
      "Lusaka": "9",
      "Muchinga": "10"
    },
    'Burundi': {
      "Buhumuza": "01",
      "Bujumbura": "02",
      "Burunga": "03",
      "Butanyerera": "04",
      "Gitega": "05"
    },
    'Rwanda': {
      "City of Kigali": "01",
      "Eastern": "02",
      "Northern": "03",
      "Southern": "04",
      "Western": "05"
    },
    'Uganda': {
      "Northern": "01",
      "Eastern": "02",
      "Central": "03",
      "Western": "04"
    },
    'Malawi': {
      "Northern": "01",
      "Central": "02",
      "Southern": "03"
    },
    'Angola': {
      "Bengo": "01",
      "Benguela": "02",
      "Bié": "03",
      "Cabinda": "04",
      "Cuando Cubango": "05",
      "Cuanza Norte": "07",
      "Cuanza Sul": "08",
      "Cunene": "09",
      "Huambo": "10",
      "Huíla": "11",
      "Luanda": "13",
      "Lunda Norte": "14",
      "Lunda Sul": "15",
      "Malanje": "16",
      "Moxico": "17",
      "Namibe": "19",
      "Uíge": "20",
      "Zaire": "21"
    },
    'Burkina Faso': {
      "Boucle du Mouhoun": "01",
      "Cascades": "02",
      "Centre": "03",
      "Centre-Est": "04",
      "Centre-Nord": "05",
      "Centre-Ouest": "06",
      "Centre-Sud": "07",
      "Est": "08",
      "Hauts-Bassins": "09",
      "Nord": "10",
      "Plateau-Central": "11",
      "Sahel": "12",
      "Sud-Ouest": "13"
    },
    'Eswatini': {
      "Hhohho": "01",
      "Manzini": "02",
      "Lubombo": "03",
      "Shiselweni": "04"
    },
    'Lesotho': {
      "Berea": "01",
      "Butha-Buthe": "02",
      "Leribe": "03",
      "Mafeteng": "04",
      "Maseru": "05",
      "Mohale's Hoek": "06",
      "Mokhotlong": "07",
      "Qacha's Nek": "08",
      "Quthing": "09",
      "Thaba-Tseka": "10"
    },
    'Tanzania': {
      "Arusha": "01",
      "Dar es Salaam": "02",
      "Dodoma": "03",
      "Geita": "04",
      "Iringa": "05",
      "Kagera": "06",
      "Katavi": "07",
      "Kigoma": "08",
      "Kilimanjaro": "09",
      "Lindi": "10",
      "Manyara": "11",
      "Mara": "12",
      "Mbeya": "13",
      "Mjini Magharibi (Zanzibar Urban/West)": "14",
      "Morogoro": "15",
      "Mtwara": "16",
      "Mwanza": "17",
      "Njombe": "18",
      "Pemba North": "19",
      "Pemba South": "20",
      "Pwani (Coast)": "21",
      "Rukwa": "22",
      "Ruvuma": "23",
      "Shinyanga": "24",
      "Simiyu": "25",
      "Singida": "26",
      "Songwe": "27",
      "Tabora": "28",
      "Tanga": "29"
    },
    'Cameroon': {
      "Adamawa": "01",
      "Centre": "02",
      "East": "03",
      "Far North": "04",
      "Littoral": "05",
      "North": "06",
      "North-West": "07",
      "West": "08",
      "South": "09",
      "South-West": "10"
    },
'Democratic Republic of the Congo': {
  "Bas-Uele": "01",
  "Equateur": "02",
  "Haut-Katanga": "03",
  "Haut-Lomami": "04",
  "Haut-Uele": "05",
  "Ituri": "06",
  "Kasai": "07",
  "Kasai-Central": "08",
  "Kasai-Oriental": "09",
  "Kinshasa": "10",
  "Kongo-Central": "11",
  "Kwango": "12",
  "Kwilu": "13",
  "Lomami": "14",
  "Lualaba": "15",
  "Mai-Ndombe": "16",
  "Maniema": "17",
  "Mongala": "18",
  "Nord-Kivu": "19",
  "Nord-Ubangi": "20",
  "Sankuru": "21",
  "Sud-Kivu": "22",
  "Sud-Ubangi": "23",
  "Tanganyika": "24",
  "Tshopo": "25",
  "Tshuapa": "26"
},
'Congo (Kinshasa/DRC)': { // Add this as an alias
  "Bas-Uele": "01",
  "Equateur": "02",
  "Haut-Katanga": "03",
  "Haut-Lomami": "04",
  "Haut-Uele": "05",
  "Ituri": "06",
  "Kasai": "07",
  "Kasai-Central": "08",
  "Kasai-Oriental": "09",
  "Kinshasa": "10",
  "Kongo-Central": "11",
  "Kwango": "12",
  "Kwilu": "13",
  "Lomami": "14",
  "Lualaba": "15",
  "Mai-Ndombe": "16",
  "Maniema": "17",
  "Mongala": "18",
  "Nord-Kivu": "19",
  "Nord-Ubangi": "20",
  "Sankuru": "21",
  "Sud-Kivu": "22",
  "Sud-Ubangi": "23",
  "Tanganyika": "24",
  "Tshopo": "25",
  "Tshuapa": "26"
},
    'Ethiopia': {
      "Addis Ababa": "01",
      "Afar": "02",
      "Amhara": "03",
      "Benishangul-Gumuz": "04",
      "Dire Dawa": "05",
      "Gambela": "06",
      "Harari": "07",
      "Oromia": "08",
      "Sidama": "09",
      "Somali": "10",
      "Southern Nations (SNNPR)": "11",
      "Tigray": "12"
    },
  'Côte d\'Ivoire (Ivory Coast)': {
      "Abidjan": "1",
      "Yamoussoukro": "2",
      "Bas-Sassandra": "3",
      "Comoé": "4",
      "Denguélé": "5",
      "Gôh-Djiboua": "6",
      "Lacs": "7",
      "Lagunes": "8",
      "Montagnes": "9",
      "Sassandra-Marahoué": "10",
      "Savanes": "11",
      "Vallée du Bandama": "12",
      "Woroba": "13",
      "Zanzan": "14"
    },
    'Ghana': {
      "WESTERN": "1",
      "CENTRAL": "2",
      "GREATER ACCRA": "3",
      "VOLTA": "4",
      "EASTERN": "5",
      "ASHANTI": "6",
      "WESTERN NORTH": "7",
      "AHAFO": "8",
      "BONO": "9",
      "BONO EAST": "10",
      "OTI": "11",
      "NORTHERN": "12",
      "SAVANNAH": "13",
      "UPPER WEST": "14",
      "NORTH EAST": "15",
      "UPPER EAST": "16"
    },
    'Mozambique': {
      "Cabo Delgado": "1",
      "Gaza": "2",
      "Inhambane": "3",
      "Manica": "4",
      "Maputo City": "5",
      "Maputo": "6",
      "Nampula": "7",
      "Niassa": "8",
      "Sofala": "9",
      "Tete": "10",
      "Zambezia": "11"
    },
    'Nigeria': {
      "North Central (NC)": "1",
      "North East (NE)": "2",
      "North West (NW)": "3",
      "South East (SE)": "4",
      "South South (SS)": "5",
      "South West (SW)": "6"
    },
    // ... other country mappings ...
    'Senegal': {
      "Dakar": "01",
      "Diourbel": "02",
      "Fatick": "03",
      "Kaffrine": "04",
      "Kaolack": "05",
      "Kedougou": "06",
      "Kolda": "07",
      "Louga": "08",
      "Matam": "09",
      "Saint-Louis": "10",
      "Sedhiou": "11",
      "Tambacounda": "12",
      "Thies": "13",
      "Ziguinchor": "14"
    },
  };
 // Handle case where country might be "Congo (Kinshasa/DRC)"
 const normalizedCountry = country === "Congo (Kinshasa/DRC)" 
 ? "Democratic Republic of the Congo" 
 : country;

if (!countryMappings[normalizedCountry]) {
 return {
   "-": "No regions available"
 };
}

  return {
    ...countryMappings[country],
  };
};

const EnterpriseSection = ({ regions, country, onTotalCalculated }) => {
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [totalRegistrations, setTotalRegistrations] = useState(0);
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const { selectedCountry } = useCountry();

  const [currentChunkTotal, setCurrentChunkTotal] = useState(0);
  const [reportSelections, setReportSelections] = useState({
    COUNTRY: "-",
    REGION: "-",
    DISTRICT: "-",
    MUNICIPALITY: "-",
    SMMEs: "-",
    "VALUE CHAIN": "-",
    PROJECT: "-",
    "USER TYPE": "-"
  });
  const [filteredTotal, setFilteredTotal] = useState(0);
  const currentCountry = selectedCountry?.name || 'South Africa';
  const updateStats = async (country, region, smmeType, landSize) => {
    const statsRef = doc(db, "stats", "registrations");
    
    try {
      await runTransaction(db, async (transaction) => {
        const statsDoc = await transaction.get(statsRef);
        let statsData = statsDoc.exists() ? statsDoc.data() : {};
        
        const countryKey = country === 'South Africa' ? 'South Africa' : country;
        
        if (!statsData[countryKey]) {
          statsData[countryKey] = {};
        }
        
        const regionKey = country === 'South Africa' 
          ? getRegionName(region, 'South Africa')
          : region;
        
        if (!statsData[countryKey][regionKey]) {
          statsData[countryKey][regionKey] = {
            total: 0,
            landSizeTotal: 0,
            bySmmetype: {}
          };
        }
        
        if (!statsData[countryKey][regionKey].bySmmetype[smmeType]) {
          statsData[countryKey][regionKey].bySmmetype[smmeType] = {
            count: 0,
            landSizeTotal: 0
          };
        }
        
        statsData[countryKey][regionKey].total = 
          (statsData[countryKey][regionKey].total || 0) + 1;
        statsData[countryKey][regionKey].landSizeTotal = 
          (statsData[countryKey][regionKey].landSizeTotal || 0) + landSize;
        statsData[countryKey][regionKey].bySmmetype[smmeType].count = 
          (statsData[countryKey][regionKey].bySmmetype[smmeType].count || 0) + 1;
        statsData[countryKey][regionKey].bySmmetype[smmeType].landSizeTotal = 
          (statsData[countryKey][regionKey].bySmmetype[smmeType].landSizeTotal || 0) + landSize;
        
        transaction.set(statsRef, statsData);
      });
    } catch (error) {
      console.error("Transaction failed:", error);
    }
  };

  useEffect(() => {
    const unsubscribe = onSnapshot(doc(db, "registrations", "current_selection"), (doc) => {
      if (doc.exists()) {
        const selection = doc.data();
        const region = getRegionName(selection.REGION, selection.COUNTRY || country);
        const smmeType = selection.SMMEs?.value || selection.SMMEs;
        const landSize = (selection.landData?.type1?.size || 0) + (selection.landData?.type2?.size || 0);
        
        if (region && smmeType) {
          setReportData(prev => {
            const newData = {...prev};
            
            if (!newData[region]) {
              newData[region] = {
                total: 0,
                landSizeTotal: 0,
                bySmmetype: {}
              };
            }
            
            if (!newData[region].bySmmetype[smmeType]) {
              newData[region].bySmmetype[smmeType] = {
                count: 0,
                landSizeTotal: 0
              };
            }
            
            newData[region].total += 1;
            newData[region].landSizeTotal += landSize;
            newData[region].bySmmetype[smmeType].count += 1;
            newData[region].bySmmetype[smmeType].landSizeTotal += landSize;
            
            return newData;
          });
          
          updateStats(selection.COUNTRY || country, region, smmeType, landSize);
        }
      }
    });

    return () => unsubscribe();
  }, [country]);

  const fullRegionMap = getRegionMap(currentCountry);
  const allRegions = Object.keys(fullRegionMap)
    .filter(region => !region.startsWith('-'));

  const chunkSize = 9;
  const regionChunks = [];

  for (let i = 0; i < allRegions.length; i += chunkSize) {
    const chunk = allRegions.slice(i, i + chunkSize);
    while (chunk.length < chunkSize) {
      chunk.push(``);
    }
    regionChunks.push(chunk);
  }

  const getRegionName = (regionData, country = 'South Africa') => {
    if (!regionData) return null;
    
    if (country === 'South Africa') {
      const regionMap = {
        "Gauteng": "013",
        "Eastern Cape": "032", 
        "Limpopo": "033",
        "KwaZulu-Natal": "044",
        "Mpumalanga": "035",
        "Western Cape": "036",
        "North West": "037",
        "Northern Cape": "038",
        "Free State": "309"
      };
      
      if (typeof regionData === 'object') {
        if (regionData.name) {
          return regionData.name;
        }
        return Object.keys(regionMap).find(key => 
          regionMap[key] === regionData.value
        ) || regionData;
      }
      
      if (typeof regionData === 'string') {
        return Object.keys(regionMap).find(key => 
          key.toLowerCase() === regionData.toLowerCase()
        ) || regionData;
      }
    }
    
    return typeof regionData === 'object' ? regionData.name : regionData;
  };

  const countryMapImages = {
    'Angola': 'https://res.cloudinary.com/dzngpgki4/image/upload/v1750444884/Angola_Map_qri8d9.jpg',
    'Botswana': 'https://res.cloudinary.com/dzngpgki4/image/upload/v1750444904/Botswana_Map_ixujke.jpg',
    'Cameroon': 'https://res.cloudinary.com/dzngpgki4/image/upload/v1750444883/Cameroon_Map_oli1ea.jpg',
    'Côte d\'Ivoire (Ivory Coast)': 'https://res.cloudinary.com/dzngpgki4/image/upload/v1750444885/Cote_D_Ivorie_Map_titbtu.jpg',
    'Congo (Kinshasa/DRC)': 'https://res.cloudinary.com/dzngpgki4/image/upload/v1750444894/Congo_DRC_map_eooyiv.jpg',
    'Eswatini': 'https://res.cloudinary.com/dzngpgki4/image/upload/v1750444890/Eswatini_Map_qg49ik.jpg',
    'Ethiopia': 'https://res.cloudinary.com/dzngpgki4/image/upload/v1750444894/Ethiopia_map_ijxizh.jpg',
    'Ghana': 'https://res.cloudinary.com/dzngpgki4/image/upload/v1750444895/Ghana_Map_at9tji.jpg',
    'Kenya': 'https://res.cloudinary.com/dzngpgki4/image/upload/v1750444894/Kenya_Map_axuptr.jpg',
    'Lesotho': 'https://res.cloudinary.com/dzngpgki4/image/upload/v1750444894/Lesotho_Map_wwweao.jpg',
    'Malawi': 'https://res.cloudinary.com/dzngpgki4/image/upload/v1750444895/Malawi_Map_cezzt0.jpg',
    'Mozambique': 'https://res.cloudinary.com/dzngpgki4/image/upload/v1750444889/Mozambique_map_mr5gti.jpg',
    'Namibia': 'https://res.cloudinary.com/dzngpgki4/image/upload/v1750444889/Namibia_Map_i2r8th.jpg',
    'Nigeria': 'https://res.cloudinary.com/dzngpgki4/image/upload/v1750444890/Nigeria_Map_fyxypv.jpg',
    'Rwanda': 'https://res.cloudinary.com/dzngpgki4/image/upload/v1750447501/Rwanda_Map_dslxiy.jpg',
    'Senegal': 'https://res.cloudinary.com/dzngpgki4/image/upload/v1750444882/Senegal_Map_gbxrbb.jpg',
    'Somalia': 'https://res.cloudinary.com/dzngpgki4/image/upload/v1750444942/Somalia_Map_xwxy02.jpg',
    'South Africa': 'https://res.cloudinary.com/dzngpgki4/image/upload/v1750444882/South_Africa_map_fhljpi.jpg',
    'South Sudan': 'https://res.cloudinary.com/dzngpgki4/image/upload/v1750444882/South_Sudan_Map_ox0wyh.jpg',
    'Tanzania': 'https://res.cloudinary.com/dzngpgki4/image/upload/v1750447501/Tanzania_map_pwlaay.jpg',
    'Uganda': 'https://res.cloudinary.com/dzngpgki4/image/upload/v1750444883/Uganda_map_rrjk9p.jpg',
    'Zambia': 'https://res.cloudinary.com/dzngpgki4/image/upload/v1750444885/Zambia_Map_izofpt.jpg'
  };

  const getSmmetype = (smmeData) => {
    if (!smmeData) return null;
    if (typeof smmeData === 'string') return smmeData;
    return smmeData.value || null;
  };

  const getCount = (region, key, field = 'count') => {
    if (region === "-" || region.startsWith('-') || !reportData || !reportData[region]) {
      return 0;
    }
    
    if (!key) {
      return reportData[region][field] || 0;
    }
    
    const validCategories = ['ESC', 'EMS', 'EFC', 'ECF', 'ENS', 'EAE', 'EYW', 'OTHERS'];
    if (!validCategories.includes(key)) {
      return 0;
    }
    
    return reportData[region].bySmmetype[key]?.[field] || 0;
  };

  const getTotalForAllRegions = (key, field = 'count', currentChunkRegions) => {
    if (!reportData) return 0;
    
    let total = 0;
    currentChunkRegions.forEach(region => {
      if (region === "-" || region.startsWith('-')) return;
      
      const count = getCount(region, key, field);
      if (typeof count === 'number' && !isNaN(count)) {
        total += count;
      }
    });
    return total;
  };

  useEffect(() => {
    const fetchData = async (selection) => {
      try {
        setLoading(true);
        
        const statsDoc = await getDoc(doc(db, "stats", "registrations"));
        if (statsDoc.exists()) {
          setReportData(statsDoc.data());
          setLoading(false);
          return;
        }
  
        const registrationsSnapshot = await getDocs(collection(db, "registrations"));
        
        const data = {};
        const allRegions = Object.keys(getRegionMap(selectedCountry));
        
        allRegions.forEach(region => {
          data[region] = {
            total: 0,
            landSizeTotal: 0,
            bySmmetype: {}
          };
               const q = query(
            collection(db, "registrations"),
            where("country", "==", country.name)
          );
          SMMEsOptions.forEach(category => {
            category.items.forEach(item => {
              data[region].bySmmetype[item.value] = {
                count: 0,
                landSizeTotal: 0
              };
            });
          });
        });
  
        registrationsSnapshot.forEach(doc => {
          const regData = doc.data();
          const regionName = getRegionName(regData.REGION, selectedCountry);
          
          if (!regionName || !data[regionName]) return;
  
          const landSize = 
            (regData.landData?.type1?.size || 0) + 
            (regData.landData?.type2?.size || 0);
  
          const smmeType = regData.SMMEs?.value || regData.SMMEs;
          
          data[regionName].total += 1;
          data[regionName].landSizeTotal += landSize;
  
          if (smmeType) {
            if (!data[regionName].bySmmetype[smmeType]) {
              data[regionName].bySmmetype[smmeType] = {
                count: 0,
                landSizeTotal: 0
              };
            }
            
            data[regionName].bySmmetype[smmeType].count += 1;
            data[regionName].bySmmetype[smmeType].landSizeTotal += landSize;
          }
        });
  
        setReportData(data);
      } catch (error) {
        console.error("Error fetching reports:", error);
        setError("Failed to load report data");
      } finally {
        setLoading(false);
      }
    };
  
    fetchData();
  }, [selectedCountry]);

  useEffect(() => {
    const unsubscribe = onSnapshot(doc(db, "registrations", "current_selection"), (doc) => {
      if (doc.exists()) {
        const selection = doc.data();
        setReportSelections(prev => ({
          ...prev,
          ...selection
        }));
        
        const region = getRegionName(selection.REGION, selection.COUNTRY || country);
        const smmeType = selection.SMMEs?.value || selection.SMMEs;
        
        if (region && smmeType) {
          setReportData(prev => {
            const newData = {...prev};
            
            if (!newData[region]) {
              newData[region] = {
                total: 0,
                landSizeTotal: 0,
                bySmmetype: {}
              };
            }
            
            if (!newData[region].bySmmetype[smmeType]) {
              newData[region].bySmmetype[smmeType] = {
                count: 0,
                landSizeTotal: 0
              };
            }
            
            newData[region].total += 1;
            newData[region].bySmmetype[smmeType].count += 1;
            
            return newData;
          });
        }
      }
    });

    return () => unsubscribe();
  }, [country]);

  const totalEntrepreneurs = useMemo(() => {
    if (!reportData) return 0;
    return Object.keys(reportData).reduce((sum, region) => {
      return sum + (reportData[region]?.total || 0);
    }, 0);
  }, [reportData]);

  useEffect(() => {
    onTotalCalculated(totalEntrepreneurs);
  }, [totalEntrepreneurs, onTotalCalculated]);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      const q = query(
        collection(db, "registrations"),
        where("COUNTRY", "==", selectedCountry)
      );
      
      const registrationsSnapshot = await getDocs(q);
      
      const data = {};
      const allRegions = Object.keys(getRegionMap(selectedCountry));
      
      allRegions.forEach(region => {
        data[region] = {
          total: 0,
          landSizeTotal: 0,
          bySmmetype: {
            'ESC': { count: 0, landSizeTotal: 0 },
            'EMS': { count: 0, landSizeTotal: 0 },
            'EFC': { count: 0, landSizeTotal: 0 },
            'ECF': { count: 0, landSizeTotal: 0 },
            'ENS': { count: 0, landSizeTotal: 0 },
            'EAE': { count: 0, landSizeTotal: 0 },
            'EYW': { count: 0, landSizeTotal: 0 },
            'OTHERS': { count: 0, landSizeTotal: 0 }
          }
        };
      });

      registrationsSnapshot.forEach(doc => {
        const regData = doc.data();
        const regionName = getRegionName(regData.REGION, selectedCountry);
        
        if (!regionName || !data[regionName]) return;

        const landSize = 
          (regData.landData?.type1?.size || 0) + 
          (regData.landData?.type2?.size || 0);

        const smmeType = regData.SMMEs?.value || regData.SMMEs || 'OTHERS';
        
        if (!data[regionName].bySmmetype[smmeType]) {
          data[regionName].bySmmetype[smmeType] = {
            count: 0,
            landSizeTotal: 0
          };
        }
        
        data[regionName].total += 1;
        data[regionName].landSizeTotal += landSize;
        data[regionName].bySmmetype[smmeType].count += 1;
        data[regionName].bySmmetype[smmeType].landSizeTotal += landSize;
      });

      setReportData(data);
    } catch (error) {
      console.error("Error fetching reports:", error);
      setError("Failed to load report data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        let queryConstraints = [];
        
        if (reportSelections.COUNTRY) {
          queryConstraints.push(where("COUNTRY", "==", reportSelections.COUNTRY));
        }
        
        if (reportSelections.REGION && reportSelections.REGION !== "-") {
          queryConstraints.push(where("REGION", "==", reportSelections.REGION));
        }
        
        if (reportSelections.SMMEs && reportSelections.SMMEs !== "-") {
          const smmeValue = typeof reportSelections.SMMEs === 'object' ? 
            reportSelections.SMMEs.value : reportSelections.SMMEs;
          queryConstraints.push(where("SMMEs", "==", smmeValue));
        }
        
        const q = query(collection(db, "registrations"), ...queryConstraints);
        await getDocs(q);
        
      } catch (error) {
        console.error("Error fetching data:", error);
        setError("Failed to load data");
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [reportSelections]);

  useEffect(() => {
    if (reportSelections.REGION && reportSelections.REGION !== "-") {
      const regionName = getRegionName(reportSelections.REGION, reportSelections.COUNTRY);
      const regionData = reportData?.[regionName];
      setFilteredTotal(regionData?.total || 0);
    } else {
      const countryTotal = Object.keys(reportData || {}).reduce((sum, region) => {
        return sum + (reportData[region]?.total || 0);
      }, 0);
      setFilteredTotal(countryTotal);
    }
  }, [reportSelections.REGION, reportSelections.COUNTRY, reportData]);

  useEffect(() => {
    onTotalCalculated(filteredTotal);
  }, [filteredTotal, onTotalCalculated]);

  useEffect(() => {
    if (reportData) {
      const total = regionChunks.reduce((total, chunk) => {
        return total + chunk.reduce((chunkTotal, region) => {
          return chunkTotal + (reportData[region]?.total || 0);
        }, 0);
      }, 0);
      setCurrentChunkTotal(total);
      onTotalCalculated(total);
    }
  }, [reportData, regionChunks]);

  useEffect(() => {
    if (reportData) {
      const total = Object.values(reportData).reduce((sum, region) => {
        return sum + (region?.total || 0);
      }, 0);
      setTotalRegistrations(total);
      onTotalCalculated(total);
    }
  }, [reportData]);

  if (loading) return <div>Loading report data...</div>;
  if (error) return <div>Error: {error}</div>;
  if (!reportData) return <div>No report data available</div>;

  const sections = [
   
    {
      title: "",
      items: [
        {label: 'Smallholder Commercial Farms (ESC)', key: 'ESC',},
        {label: 'Land size (HA)', key: 'ESC', field: 'landSizeTotal', color: '#3F8325'},
      ]
    },
    {
      title: "",
      items: [
        {label: 'Medium-Scale Farms (EMS)', key: 'EMS', },
        {label: 'Land size (HA)',key: 'EMS', color: '#03897B'},
      ]
    },
    {
      title: "",
      items: [
        {label: 'Farmer Cooperatives/Producer Organizations (EFC)', key: 'EFC',},
        {label: 'Land size (HA)', key: 'EFC', color: '#CADF4E'},
      ]
    },
    {
      title: "",
      items: [
        {label: 'Contract Farming SMEs (ECF)', key: 'ECF', },
        {label: 'Land size (HA)',  key: 'ECF', color: '#8EC543'},
      ]
    },
    {
      title: "",
      items: [
        {label: 'Niche or Specialized Farms (ENS)', key: 'ENS', },
        {label: 'Land size (HA)', key: 'ENS', color: '#F7B030'},
      ]
    },
    {
      title: "",
      items: [
        {label: 'Agri-Entrepreneurs with Integrated Operations (EAE)', key: 'EAE', },
        {label: 'Land size (HA)', key: 'EAE', color: '#C4C4C4'},
      ]
    },
    {
      title: "",
      items: [
        {label: 'Youth or Women-led Farming Startups (EYW)', key: 'EYW', },
        {label: 'Land size (HA)', key: 'EYW', color: '#000000'},
      ]
    },
  ];
  
  return (
    <div className="mb-6 md:mt-10 mt-3">
      {(() => {
        const totalEntrepreneurs = regionChunks.reduce((total, chunk) => {
          return total + chunk.reduce((chunkTotal, region) => {
            return chunkTotal + (reportData[region]?.total || 0);
          }, 0);
        }, 0);
        
        return (
          <div className="hidden" data-total={totalEntrepreneurs}></div>
        );
      })()}
      
      {regionChunks.map((regionChunk, chunkIndex) => (
        <div key={`chunk-${chunkIndex}`} className="mb-8">
          <div className="flex md:flex-row items-start gap-1 md:gap-2">
            <div className="w-[90px] md:w-[220px]">
              <p className="h-4 md:h-7 text-[#152C24] rounded-md text-[5px] md:text-[16px] font-bold">AGRICULTURAL  SMMEs</p>
            </div>
            <p className="h-4 md:h-7 text-[#152C24] rounded-md text-[4px] md:text-[16px] font-bold">TOTAL</p>

            <div className="grid grid-cols-9 md:grid-cols-9 md:gap-[10px] gap-[20px] flex-1">
              {regionChunk.map((region, i) => (
                <div key={i} className="text-[4px] md:text-[14px] rounded-[5px] text-black font-extrabold px-1 text-center md:w-[100px]">
                  {region}
                </div>
              ))}
            </div>
          </div>
          
          <p className="text-[#152C24] font-bold mb-2 text-[10px] md:text-[14px]">TOTAL</p>
          <div className="mt-2">
            <div className='md:h-[2px] h-[1px] bg-black'></div>

            <div className="overflow-x-auto">
              <div className="md:w-auto grid md:grid-cols-[300px_repeat(9,1fr)] grid-cols-[120px_repeat(9,1fr)] gap-y-1 gap-x-1 md:gap-x-2 mt-2 md:mt-3">
                <div className="flex items-center gap-1   md:w-[300px]">
                  <div className="text-[6px] md:text-[14px] text-black w-[180px]  font-bold leading-tight">
                    Agricultural  Entrepreneurs
                  </div>
                  <div
              className="md:w-[34px] w-[15px] h-[15px] md:h-[34px] bg-[#FFBD26]  rounded-full cursor-pointer"
              title="Highlighted Expense"
            />
                  <div className="h-4 md:h-7 text-[#152C24] md:ml-[30px] ml-[25px]  font-bold rounded-md text-[5px] md:text-[14px] flex items-center justify-center ">
                    {getTotalForAllRegions(null, 'total', regionChunk)}
                  </div>
                </div>
                {regionChunk.map((region, j) => (
                  <div
                    key={`total-${j}`}
                    className="h-4 md:h-7 bg-[#CFD7D7] rounded-md text-[5px] md:text-[16px] flex items-center justify-center font-bold"
                  >
                    {getCount(region, null, 'total')}
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="">
            <div className="overflow-x-auto">
              <div className="md:w-auto grid md:grid-cols-[300px_repeat(9,1fr)] grid-cols-[120px_repeat(9,1fr)] gap-y-1 gap-x-1 md:gap-x-2 mt-1 md:mt-2">
                <div className="flex items-center gap-[34px]  ">
                  <div className="text-[6px] md:text-[14px] w-[150px]  text-black font-bold leading-tight">
                  Land for Farming (Ha)
                  </div>
                  <div
  className="md:w-[34px] w-[15px] h-[15px] md:h-[34px] bg-[#2E453D]    rounded-full cursor-pointer"
  title="Highlighted Expense"
/>
                  <div className="h-4 md:h-7 text-[#152C24] rounded-md font-bold   text-[5px] md:text-[14px] flex items-center justify-center ">
                    {regionChunk.reduce((total, region) => {
                      return total + (reportData[region]?.landSizeTotal || 0);
                    }, 0)}
                  </div>
                </div>
                {regionChunk.map((region) => (
                  <div 
                    key={`total-${region}`}
                    className="h-4 md:h-7 bg-[#CFD7D7] rounded-md text-[5px] md:text-[16px] flex items-center justify-center font-normal"
                  >
                    {reportData[region]?.landSizeTotal || 0}
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div className="mt-8 ">

            <div className="overflow-x-auto">
              <div className="md:w-auto grid md:grid-cols-[300px_repeat(9,1fr)] grid-cols-[120px_repeat(9,1fr)] gap-y-1 gap-x-1 md:gap-x-2 mt-2 md:mt-3">
                <div className="flex items-center gap-8   w-[280px]">
                  <div className="text-[6px] md:text-[14px] text-black w-[120px] font-bold leading-tight">
                  Production Cost
                  </div>
                  <div
              className="md:w-[34px] w-[15px] h-[15px] md:h-[34px] bg-[#9FAFAF]  ml-8  rounded-full cursor-pointer"
              title="Highlighted Expense"
            />
                  <div className="h-4 md:h-7 text-[#152C24] rounded-md font-bold  text-[5px] md:text-[14px] flex items-center justify-center ">
                    {getTotalForAllRegions(null, 'total', regionChunk)}
                  </div>
                </div>
                {regionChunk.map((region, j) => (
                  <div
                    key={`total-${j}`}
                    className="h-4 md:h-7 bg-[#CFD7D7] rounded-md  text-[5px] md:text-[16px] flex items-center justify-center font-normal"
                  >
                    {getCount(region, null, 'total')}
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div className="mt-2">

<div className="overflow-x-auto">
  <div className="md:w-auto grid md:grid-cols-[300px_repeat(9,1fr)] grid-cols-[120px_repeat(9,1fr)] gap-y-1 gap-x-1 md:gap-x-2 mt-2 md:mt-3">
    <div className="flex items-center gap-8   w-[300px]">
      <div className="text-[6px] md:text-[14px] text-black w-[120px] font-bold leading-tight">
      Revenue
      </div>
      <div
  className="md:w-[34px] w-[15px] h-[15px] md:h-[34px] bg-[#CFD7D7]  ml-8  rounded-full cursor-pointer"
  title="Highlighted Expense"
/>
      <div className="h-4 md:h-7 text-[#152C24] rounded-md text-[5px] font-bold  md:text-[14px] flex items-center justify-center ">
        {getTotalForAllRegions(null, 'total', regionChunk)}
      </div>
    </div>
    {regionChunk.map((region, j) => (
      <div
        key={`total-${j}`}
        className="h-4 md:h-7 bg-[#CFD7D7] rounded-md text-[5px] md:text-[16px] flex items-center justify-center  font-normal"
      >
        {getCount(region, null, 'total')}
      </div>
    ))}
  </div>
</div>
</div>
            <p className="h-4 mt-10 md:h-7 text-[#152C24] rounded-md text-[5px] md:text-[16px] font-bold">ENTERPRISE</p>

          {sections.map((section, index) => (
            <div key={index} className="">
              <p className="text-[#152C24] font-bold mb-2 text-[10px] md:text-[14px]">
                {section.title}
              </p>
              <div className='md:h-[1.5px] h-[1px] bg-black'></div>

              <div className="overflow-x-auto">
                <div className="md:w-auto grid md:grid-cols-[200px_90px_repeat(9,1fr)] grid-cols-[90px_25px_repeat(9,1fr)] gap-y-1 gap-x-1 md:gap-x-2 mt-2 md:mt-3">
                {section.items.map((item, i) => (
  <React.Fragment key={`${section.title}-${item.key}-${i}`}>
    <div className="flex items-center gap-5 w-[300px]">
      <div className={`text-[6px] md:text-[14px] w-[170px] text-black leading-tight ${i === 0 ? 'font-bold' : 'font-normal'}`}>
        {item.label}
      </div>
      {/* Removed the i === 0 condition so circle shows for all items */}
      {item.color && (
        <div 
          className="w-[6px] h-[6px] md:w-[36px] md:h-[36px] rounded-full mr-1 md:mr-2"
          style={{ backgroundColor: item.color }}
        ></div>
      )}
    </div>
    <div className="h-4 md:h-7 text-[#152C24]  rounded-md ml-9 text-[5px] md:text-[14px] flex items-center justify-center font-normal">
      {getTotalForAllRegions(item.key, item.field || 'count', regionChunk)}
    </div>
    {regionChunk.map((region, j) => (
      <div
        key={`${section.title}-${item.key}-${region}-${j}`}
        className="h-4 md:h-7 bg-[#CFD7D7] rounded-md text-[5px] md:text-[16px] flex items-center justify-center"
      >
        {getCount(region, item.key, item.field || 'count')}
      </div>
    ))}
  </React.Fragment>
))}
                </div>
              </div>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
};
const EnterpriseSection1 = ({ regions, country, onTotalCalculated }) => {
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [totalRegistrations, setTotalRegistrations] = useState(0);
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const { selectedCountry } = useCountry();
  
  const [currentChunkTotal, setCurrentChunkTotal] = useState(0);
  const [reportSelections, setReportSelections] = useState({
    COUNTRY: "-",
    REGION: "-",
    DISTRICT: "-",
    MUNICIPALITY: "-",
    SMMEs: "-",
    "VALUE CHAIN": "-",
    PROJECT: "-",
    "USER TYPE": "-"
  });
  const [filteredTotal, setFilteredTotal] = useState(0);

  const updateStats = async (country, region, smmeType, landSize) => {
    const statsRef = doc(db, "stats", "registrations");
    
    try {
      await runTransaction(db, async (transaction) => {
        const statsDoc = await transaction.get(statsRef);
        let statsData = statsDoc.exists() ? statsDoc.data() : {};
        
        const countryKey = country === 'South Africa' ? 'South Africa' : country;
        
        if (!statsData[countryKey]) {
          statsData[countryKey] = {};
        }
        
        const regionKey = country === 'South Africa' 
          ? getRegionName(region, 'South Africa')
          : region;
        
        if (!statsData[countryKey][regionKey]) {
          statsData[countryKey][regionKey] = {
            total: 0,
            landSizeTotal: 0,
            bySmmetype: {}
          };
        }
        
        if (!statsData[countryKey][regionKey].bySmmetype[smmeType]) {
          statsData[countryKey][regionKey].bySmmetype[smmeType] = {
            count: 0,
            landSizeTotal: 0
          };
        }
        
        statsData[countryKey][regionKey].total = 
          (statsData[countryKey][regionKey].total || 0) + 1;
        statsData[countryKey][regionKey].landSizeTotal = 
          (statsData[countryKey][regionKey].landSizeTotal || 0) + landSize;
        statsData[countryKey][regionKey].bySmmetype[smmeType].count = 
          (statsData[countryKey][regionKey].bySmmetype[smmeType].count || 0) + 1;
        statsData[countryKey][regionKey].bySmmetype[smmeType].landSizeTotal = 
          (statsData[countryKey][regionKey].bySmmetype[smmeType].landSizeTotal || 0) + landSize;
        
        transaction.set(statsRef, statsData);
      });
    } catch (error) {
      console.error("Transaction failed:", error);
    }
  };

  useEffect(() => {
    const unsubscribe = onSnapshot(doc(db, "registrations", "current_selection"), (doc) => {
      if (doc.exists()) {
        const selection = doc.data();
        const region = getRegionName(selection.REGION, selection.COUNTRY || country);
        const smmeType = selection.SMMEs?.value || selection.SMMEs;
        const landSize = (selection.landData?.type1?.size || 0) + (selection.landData?.type2?.size || 0);
        
        if (region && smmeType) {
          setReportData(prev => {
            const newData = {...prev};
            
            if (!newData[region]) {
              newData[region] = {
                total: 0,
                landSizeTotal: 0,
                bySmmetype: {}
              };
            }
            
            if (!newData[region].bySmmetype[smmeType]) {
              newData[region].bySmmetype[smmeType] = {
                count: 0,
                landSizeTotal: 0
              };
            }
            
            newData[region].total += 1;
            newData[region].landSizeTotal += landSize;
            newData[region].bySmmetype[smmeType].count += 1;
            newData[region].bySmmetype[smmeType].landSizeTotal += landSize;
            
            return newData;
          });
          
          updateStats(selection.COUNTRY || country, region, smmeType, landSize);
        }
      }
    });

    return () => unsubscribe();
  }, [country]);
  const currentCountry = selectedCountry?.name || 'South Africa';

  const fullRegionMap = getRegionMap(currentCountry);
  
  const allRegions = Object.keys(fullRegionMap)
    .filter(region => !region.startsWith('-'));

  const chunkSize = 9;
  const regionChunks = [];

  for (let i = 0; i < allRegions.length; i += chunkSize) {
    const chunk = allRegions.slice(i, i + chunkSize);
    while (chunk.length < chunkSize) {
      chunk.push(``);
    }
    regionChunks.push(chunk);
  }

  const getRegionName = (regionData, country = 'South Africa') => {
    if (!regionData) return null;
    
    if (country === 'South Africa') {
      const regionMap = {
        "Gauteng": "013",
        "Eastern Cape": "032", 
        "Limpopo": "033",
        "KwaZulu-Natal": "044",
        "Mpumalanga": "035",
        "Western Cape": "036",
        "North West": "037",
        "Northern Cape": "038",
        "Free State": "309"
      };
      
      if (typeof regionData === 'object') {
        if (regionData.name) {
          return regionData.name;
        }
        return Object.keys(regionMap).find(key => 
          regionMap[key] === regionData.value
        ) || regionData;
      }
      
      if (typeof regionData === 'string') {
        return Object.keys(regionMap).find(key => 
          key.toLowerCase() === regionData.toLowerCase()
        ) || regionData;
      }
    }
    
    return typeof regionData === 'object' ? regionData.name : regionData;
  };

  const countryMapImages = {
    'Angola': 'https://res.cloudinary.com/dzngpgki4/image/upload/v1750444884/Angola_Map_qri8d9.jpg',
    'Botswana': 'https://res.cloudinary.com/dzngpgki4/image/upload/v1750444904/Botswana_Map_ixujke.jpg',
    'Cameroon': 'https://res.cloudinary.com/dzngpgki4/image/upload/v1750444883/Cameroon_Map_oli1ea.jpg',
    'Côte d\'Ivoire (Ivory Coast)': 'https://res.cloudinary.com/dzngpgki4/image/upload/v1750444885/Cote_D_Ivorie_Map_titbtu.jpg',
    'Congo (Kinshasa/DRC)': 'https://res.cloudinary.com/dzngpgki4/image/upload/v1750444894/Congo_DRC_map_eooyiv.jpg',
    'Eswatini': 'https://res.cloudinary.com/dzngpgki4/image/upload/v1750444890/Eswatini_Map_qg49ik.jpg',
    'Ethiopia': 'https://res.cloudinary.com/dzngpgki4/image/upload/v1750444894/Ethiopia_map_ijxizh.jpg',
    'Ghana': 'https://res.cloudinary.com/dzngpgki4/image/upload/v1750444895/Ghana_Map_at9tji.jpg',
    'Kenya': 'https://res.cloudinary.com/dzngpgki4/image/upload/v1750444894/Kenya_Map_axuptr.jpg',
    'Lesotho': 'https://res.cloudinary.com/dzngpgki4/image/upload/v1750444894/Lesotho_Map_wwweao.jpg',
    'Malawi': 'https://res.cloudinary.com/dzngpgki4/image/upload/v1750444895/Malawi_Map_cezzt0.jpg',
    'Mozambique': 'https://res.cloudinary.com/dzngpgki4/image/upload/v1750444889/Mozambique_map_mr5gti.jpg',
    'Namibia': 'https://res.cloudinary.com/dzngpgki4/image/upload/v1750444889/Namibia_Map_i2r8th.jpg',
    'Nigeria': 'https://res.cloudinary.com/dzngpgki4/image/upload/v1750444890/Nigeria_Map_fyxypv.jpg',
    'Rwanda': 'https://res.cloudinary.com/dzngpgki4/image/upload/v1750447501/Rwanda_Map_dslxiy.jpg',
    'Senegal': 'https://res.cloudinary.com/dzngpgki4/image/upload/v1750444882/Senegal_Map_gbxrbb.jpg',
    'Somalia': 'https://res.cloudinary.com/dzngpgki4/image/upload/v1750444942/Somalia_Map_xwxy02.jpg',
    'South Africa': 'https://res.cloudinary.com/dzngpgki4/image/upload/v1750444882/South_Africa_map_fhljpi.jpg',
    'South Sudan': 'https://res.cloudinary.com/dzngpgki4/image/upload/v1750444882/South_Sudan_Map_ox0wyh.jpg',
    'Tanzania': 'https://res.cloudinary.com/dzngpgki4/image/upload/v1750447501/Tanzania_map_pwlaay.jpg',
    'Uganda': 'https://res.cloudinary.com/dzngpgki4/image/upload/v1750444883/Uganda_map_rrjk9p.jpg',
    'Zambia': 'https://res.cloudinary.com/dzngpgki4/image/upload/v1750444885/Zambia_Map_izofpt.jpg'
  };

  const getSmmetype = (smmeData) => {
    if (!smmeData) return null;
    if (typeof smmeData === 'string') return smmeData;
    return smmeData.value || null;
  };

  const getCount = (region, key, field = 'count') => {
    if (region === "-" || region.startsWith('-') || !reportData || !reportData[region]) {
      return 0;
    }
    
    if (!key) {
      return reportData[region][field] || 0;
    }
    
    const validCategories = ['ESC', 'EMS', 'EFC', 'ECF', 'ENS', 'EAE', 'EYW', 'OTHERS'];
    if (!validCategories.includes(key)) {
      return 0;
    }
    
    return reportData[region].bySmmetype[key]?.[field] || 0;
  };

  const getTotalForAllRegions = (key, field = 'count', currentChunkRegions) => {
    if (!reportData) return 0;
    
    let total = 0;
    currentChunkRegions.forEach(region => {
      if (region === "-" || region.startsWith('-')) return;
      
      const count = getCount(region, key, field);
      if (typeof count === 'number' && !isNaN(count)) {
        total += count;
      }
    });
    return total;
  };

  useEffect(() => {
    const fetchData = async (selection) => {
      try {
        setLoading(true);
        
        const statsDoc = await getDoc(doc(db, "stats", "registrations"));
        if (statsDoc.exists()) {
          setReportData(statsDoc.data());
          setLoading(false);
          return;
        }
  
        const registrationsSnapshot = await getDocs(collection(db, "registrations"));
        
        const data = {};
        const allRegions = Object.keys(getRegionMap(selectedCountry));
        
        allRegions.forEach(region => {
          data[region] = {
            total: 0,
            landSizeTotal: 0,
            bySmmetype: {}
          };
               const q = query(
            collection(db, "registrations"),
            where("country", "==", country.name)
          );
          SMMEsOptions.forEach(category => {
            category.items.forEach(item => {
              data[region].bySmmetype[item.value] = {
                count: 0,
                landSizeTotal: 0
              };
            });
          });
        });
  
        registrationsSnapshot.forEach(doc => {
          const regData = doc.data();
          const regionName = getRegionName(regData.REGION, selectedCountry);
          
          if (!regionName || !data[regionName]) return;
  
          const landSize = 
            (regData.landData?.type1?.size || 0) + 
            (regData.landData?.type2?.size || 0);
  
          const smmeType = regData.SMMEs?.value || regData.SMMEs;
          
          data[regionName].total += 1;
          data[regionName].landSizeTotal += landSize;
  
          if (smmeType) {
            if (!data[regionName].bySmmetype[smmeType]) {
              data[regionName].bySmmetype[smmeType] = {
                count: 0,
                landSizeTotal: 0
              };
            }
            
            data[regionName].bySmmetype[smmeType].count += 1;
            data[regionName].bySmmetype[smmeType].landSizeTotal += landSize;
          }
        });
  
        setReportData(data);
      } catch (error) {
        console.error("Error fetching reports:", error);
        setError("Failed to load report data");
      } finally {
        setLoading(false);
      }
    };
  
    fetchData();
  }, [selectedCountry]);

  useEffect(() => {
    const unsubscribe = onSnapshot(doc(db, "registrations", "current_selection"), (doc) => {
      if (doc.exists()) {
        const selection = doc.data();
        setReportSelections(prev => ({
          ...prev,
          ...selection
        }));
        
        const region = getRegionName(selection.REGION, selection.COUNTRY || country);
        const smmeType = selection.SMMEs?.value || selection.SMMEs;
        
        if (region && smmeType) {
          setReportData(prev => {
            const newData = {...prev};
            
            if (!newData[region]) {
              newData[region] = {
                total: 0,
                landSizeTotal: 0,
                bySmmetype: {}
              };
            }
            
            if (!newData[region].bySmmetype[smmeType]) {
              newData[region].bySmmetype[smmeType] = {
                count: 0,
                landSizeTotal: 0
              };
            }
            
            newData[region].total += 1;
            newData[region].bySmmetype[smmeType].count += 1;
            
            return newData;
          });
        }
      }
    });

    return () => unsubscribe();
  }, [country]);

  const totalEntrepreneurs = useMemo(() => {
    if (!reportData) return 0;
    return Object.keys(reportData).reduce((sum, region) => {
      return sum + (reportData[region]?.total || 0);
    }, 0);
  }, [reportData]);

  useEffect(() => {
    onTotalCalculated(totalEntrepreneurs);
  }, [totalEntrepreneurs, onTotalCalculated]);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      const q = query(
        collection(db, "registrations"),
        where("COUNTRY", "==", selectedCountry)
      );
      
      const registrationsSnapshot = await getDocs(q);
      
      const data = {};
      const allRegions = Object.keys(getRegionMap(selectedCountry));
      
      allRegions.forEach(region => {
        data[region] = {
          total: 0,
          landSizeTotal: 0,
          bySmmetype: {
            'ESC': { count: 0, landSizeTotal: 0 },
            'EMS': { count: 0, landSizeTotal: 0 },
            'EFC': { count: 0, landSizeTotal: 0 },
            'ECF': { count: 0, landSizeTotal: 0 },
            'ENS': { count: 0, landSizeTotal: 0 },
            'EAE': { count: 0, landSizeTotal: 0 },
            'EYW': { count: 0, landSizeTotal: 0 },
            'OTHERS': { count: 0, landSizeTotal: 0 }
          }
        };
      });

      registrationsSnapshot.forEach(doc => {
        const regData = doc.data();
        const regionName = getRegionName(regData.REGION, selectedCountry);
        
        if (!regionName || !data[regionName]) return;

        const landSize = 
          (regData.landData?.type1?.size || 0) + 
          (regData.landData?.type2?.size || 0);

        const smmeType = regData.SMMEs?.value || regData.SMMEs || 'OTHERS';
        
        if (!data[regionName].bySmmetype[smmeType]) {
          data[regionName].bySmmetype[smmeType] = {
            count: 0,
            landSizeTotal: 0
          };
        }
        
        data[regionName].total += 1;
        data[regionName].landSizeTotal += landSize;
        data[regionName].bySmmetype[smmeType].count += 1;
        data[regionName].bySmmetype[smmeType].landSizeTotal += landSize;
      });

      setReportData(data);
    } catch (error) {
      console.error("Error fetching reports:", error);
      setError("Failed to load report data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        let queryConstraints = [];
        
        if (reportSelections.COUNTRY) {
          queryConstraints.push(where("COUNTRY", "==", reportSelections.COUNTRY));
        }
        
        if (reportSelections.REGION && reportSelections.REGION !== "-") {
          queryConstraints.push(where("REGION", "==", reportSelections.REGION));
        }
        
        if (reportSelections.SMMEs && reportSelections.SMMEs !== "-") {
          const smmeValue = typeof reportSelections.SMMEs === 'object' ? 
            reportSelections.SMMEs.value : reportSelections.SMMEs;
          queryConstraints.push(where("SMMEs", "==", smmeValue));
        }
        
        const q = query(collection(db, "registrations"), ...queryConstraints);
        await getDocs(q);
        
      } catch (error) {
        console.error("Error fetching data:", error);
        setError("Failed to load data");
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [reportSelections]);

  useEffect(() => {
    if (reportSelections.REGION && reportSelections.REGION !== "-") {
      const regionName = getRegionName(reportSelections.REGION, reportSelections.COUNTRY);
      const regionData = reportData?.[regionName];
      setFilteredTotal(regionData?.total || 0);
    } else {
      const countryTotal = Object.keys(reportData || {}).reduce((sum, region) => {
        return sum + (reportData[region]?.total || 0);
      }, 0);
      setFilteredTotal(countryTotal);
    }
  }, [reportSelections.REGION, reportSelections.COUNTRY, reportData]);

  useEffect(() => {
    onTotalCalculated(filteredTotal);
  }, [filteredTotal, onTotalCalculated]);

  useEffect(() => {
    if (reportData) {
      const total = regionChunks.reduce((total, chunk) => {
        return total + chunk.reduce((chunkTotal, region) => {
          return chunkTotal + (reportData[region]?.total || 0);
        }, 0);
      }, 0);
      setCurrentChunkTotal(total);
      onTotalCalculated(total);
    }
  }, [reportData, regionChunks]);

  useEffect(() => {
    if (reportData) {
      const total = Object.values(reportData).reduce((sum, region) => {
        return sum + (region?.total || 0);
      }, 0);
      setTotalRegistrations(total);
      onTotalCalculated(total);
    }
  }, [reportData]);

  if (loading) return <div>Loading report data...</div>;
  if (error) return <div>Error: {error}</div>;
  if (!reportData) return <div>No report data available</div>;

  const sections = [
   
    {
      title: "",
      items: [
        {label: 'Smallholder Commercial Farms (ESC)', key: 'ESC',},
        {label: 'Land size (HA)', key: 'ESC', field: 'landSizeTotal', color: '#3F8325'},
      ]
    },
    {
      title: "",
      items: [
        {label: 'Medium-Scale Farms (EMS)', key: 'EMS', },
        {label: 'Land size (HA)',key: 'EMS', color: '#03897B'},
      ]
    },
    {
      title: "",
      items: [
        {label: 'Farmer Cooperatives/Producer Organizations (EFC)', key: 'EFC',},
        {label: 'Land size (HA)', key: 'EFC', color: '#CADF4E'},
      ]
    },
    {
      title: "",
      items: [
        {label: 'Contract Farming SMEs (ECF)', key: 'ECF', },
        {label: 'Land size (HA)',  key: 'ECF', color: '#8EC543'},
      ]
    },
    {
      title: "",
      items: [
        {label: 'Niche or Specialized Farms (ENS)', key: 'ENS', },
        {label: 'Land size (HA)', key: 'ENS', color: '#F7B030'},
      ]
    },
    {
      title: "",
      items: [
        {label: 'Agri-Entrepreneurs with Integrated Operations (EAE)', key: 'EAE', },
        {label: 'Land size (HA)', key: 'EAE', color: '#C4C4C4'},
      ]
    },
    {
      title: "",
      items: [
        {label: 'Youth or Women-led Farming Startups (EYW)', key: 'EYW', },
        {label: 'Land size (HA)', key: 'EYW', color: '#000000'},
      ]
    },
  ];
  
  return (
    <div className="mb-6 md:mt-10 mt-3">
    {(() => {
      const totalEntrepreneurs = regionChunks.reduce((total, chunk) => {
        return total + chunk.reduce((chunkTotal, region) => {
          return chunkTotal + (reportData[region]?.total || 0);
        }, 0);
      }, 0);
      
      return (
        <div className="hidden" data-total={totalEntrepreneurs}></div>
      );
    })()}
    
    {regionChunks.map((regionChunk, chunkIndex) => (
      <div key={`chunk-${chunkIndex}`} className="mb-8">
        {/* Header Row */}
        <div className="flex md:flex-row items-start gap-1 md:gap-2">
          <div className="w-[60px] md:w-[220px]">
            <p className="h-4 md:h-7 text-[#152C24] rounded-md text-[4px] md:text-[16px] font-bold">AGRICULTURAL SMMEs</p>
          </div>
          <p className="h-4 md:h-7 text-[#152C24] rounded-md text-[4px] md:text-[16px] font-bold">TOTAL</p>

          <div className="grid grid-cols-9 md:grid-cols-9 md:gap-[10px] gap-[2px] flex-1 overflow-x-auto">
            {regionChunk.map((region, i) => (
              <div key={i} className="text-[4px] md:text-[14px] rounded-[5px] text-black font-extrabold px-1 text-center md:w-[100px] ">
                {region}
              </div>
            ))}
          </div>
        </div>
        
        {/* Section Title */}
        <p className="text-[#152C24] font-bold mb-1 md:mb-2 text-[6px] md:text-[14px]">TOTAL</p>
        <div className="mt-1 md:mt-2">
          <div className='md:h-[2px] h-[1px] bg-black'></div>

          {/* Entrepreneurs Row */}
          <div className="overflow-x-auto">
            <div className="md:w-auto grid md:grid-cols-[300px_repeat(9,1fr)] grid-cols-[120px_repeat(9,1fr)] gap-y-1 gap-x-1 md:gap-x-2 mt-1 md:mt-3">
              <div className="flex items-center gap-1 md:w-[300px]">
                <div className="text-[4px] md:text-[14px] text-black w-[60px] md:w-[180px] font-bold leading-tight">
                  Agricultural Entrepreneurs
                </div>
                <div className="md:w-[34px] w-[10px] h-[10px] md:h-[34px] bg-[#FFBD26] rounded-full cursor-pointer"/>
                <div className="h-4 md:h-7 text-[#152C24] md:ml-[30px] ml-[5px] font-bold rounded-md text-[4px] md:text-[14px] flex items-center justify-center">
                  {getTotalForAllRegions(null, 'total', regionChunk)}
                </div>
              </div>
              {regionChunk.map((region, j) => (
                <div
                  key={`total-${j}`}
                  className="h-4 md:h-7 bg-[#CFD7D7] rounded-md text-[4px] md:text-[16px] flex items-center justify-center font-bold"
                >
                  {getCount(region, null, 'total')}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Land Size Row */}
        <div className="mt-1 md:mt-2">
          <div className="overflow-x-auto">
            <div className="md:w-auto grid md:grid-cols-[300px_repeat(9,1fr)] grid-cols-[120px_repeat(9,1fr)] gap-y-1 gap-x-1 md:gap-x-2">
              <div className="flex items-center ">
                <div className="text-[4px] md:text-[14px] w-[65px] md:w-[150px] text-black font-bold leading-tight">
                  Land (Ha)
                </div>
                <div className="md:w-[34px] w-[10px] h-[10px] md:h-[34px] bg-[#2E453D] rounded-full cursor-pointer"/>
                <div className="h-4 md:h-7 md:ml-0 ml-2 text-[#152C24] rounded-md font-bold text-[4px] md:text-[14px] flex items-center justify-center">
                  {regionChunk.reduce((total, region) => {
                    return total + (reportData[region]?.landSizeTotal || 0);
                  }, 0)}
                </div>
              </div>
              {regionChunk.map((region) => (
                <div 
                  key={`total-${region}`}
                  className="h-4 md:h-7 bg-[#CFD7D7] rounded-md text-[4px] md:text-[16px] flex items-center justify-center font-normal"
                >
                  {reportData[region]?.landSizeTotal || 0}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Production Cost Row */}
        <div className="mt-2 md:mt-8">
          <div className="overflow-x-auto">
            <div className="md:w-auto grid md:grid-cols-[300px_repeat(9,1fr)] grid-cols-[120px_repeat(9,1fr)] gap-y-1 gap-x-1 md:gap-x-2">
              <div className="flex items-center gap-1">
                <div className="text-[4px] md:text-[14px] text-black w-[60px] md:w-[120px] font-bold leading-tight">
                  Production Cost
                </div>
                <div className="md:w-[34px] w-[10px] h-[10px] md:h-[34px]  bg-[#9FAFAF] rounded-full cursor-pointer"/>
                <div className="h-4 md:h-7 text-[#152C24] rounded-md md:ml-0 ml-[5px]  font-bold text-[4px] md:text-[14px] flex items-center justify-center">
                  {getTotalForAllRegions(null, 'total', regionChunk)}
                </div>
              </div>
              {regionChunk.map((region, j) => (
                <div
                  key={`total-${j}`}
                  className="h-4 md:h-7 bg-[#CFD7D7] rounded-md text-[4px] md:text-[16px] flex items-center justify-center font-normal"
                >
                  {getCount(region, null, 'total')}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Revenue Row */}
        <div className="mt-1 md:mt-2">
          <div className="overflow-x-auto">
            <div className="md:w-auto grid md:grid-cols-[300px_repeat(9,1fr)] grid-cols-[120px_repeat(9,1fr)] gap-y-1 gap-x-1 md:gap-x-2">
              <div className="flex items-center gap-1">
                <div className="text-[4px] md:text-[14px] text-black w-[60px] md:w-[120px] font-bold leading-tight">
                  Revenue
                </div>
                <div className="md:w-[34px] w-[10px] h-[10px] md:h-[34px] bg-[#CFD7D7] rounded-full cursor-pointer"/>
                <div className="h-4 md:h-7 text-[#152C24] rounded-md md:ml-0 ml-[4px] text-[4px] md:text-[14px] flex items-center justify-center font-bold">
                  {getTotalForAllRegions(null, 'total', regionChunk)}
                </div>
              </div>
              {regionChunk.map((region, j) => (
                <div
                  key={`total-${j}`}
                  className="h-4 md:h-7 bg-[#CFD7D7] rounded-md text-[4px] md:text-[16px] flex items-center justify-center font-normal"
                >
                  {getCount(region, null, 'total')}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Enterprise Section */}
        <p className="h-4 mt-4 md:mt-20 md:h-7 text-[#152C24] rounded-md text-[4px] md:text-[16px] font-bold">ENTERPRISE</p>

        {sections.map((section, index) => (
          <div key={index} className="mt-1 md:mt-2">
            <p className="text-[#152C24] font-bold mb-1 md:mb-2 text-[8px] md:text-[14px]">
              {section.title}
            </p>
            <div className='md:h-[1.5px] h-[1px] bg-black'></div>

            <div className="overflow-x-auto">
              <div className="md:w-auto grid md:grid-cols-[200px_90px_repeat(9,1fr)] grid-cols-[90px_25px_repeat(9,1fr)] gap-y-1 gap-x-1 md:gap-x-2 mt-1 md:mt-3">
                {section.items.map((item, i) => (
                  <React.Fragment key={`${section.title}-${item.key}-${i}`}>
                    <div className="flex items-center gap-1">
                      <div className={`text-[4px] md:text-[14px] w-[60px] md:w-[170px] text-black leading-tight ${i === 0 ? 'font-bold' : 'font-normal'}`}>
                        {item.label}
                      </div>
                      {item.color && (
                        <div 
                          className="w-[6px] h-[6px] md:w-[36px] md:h-[36px] rounded-full"
                          style={{ backgroundColor: item.color }}
                        ></div>
                      )}
                    </div>
                    <div className="h-4 md:h-7 text-[#152C24] rounded-md text-[4px] md:text-[14px] flex items-center justify-center font-normal">
                      {getTotalForAllRegions(item.key, item.field || 'count', regionChunk)}
                    </div>
                    {regionChunk.map((region, j) => (
                      <div
                        key={`${section.title}-${item.key}-${region}-${j}`}
                        className="h-4 md:h-7 bg-[#CFD7D7] rounded-md text-[4px] md:text-[16px] flex items-center justify-center"
                      >
                        {getCount(region, item.key, item.field || 'count')}
                      </div>
                    ))}
                  </React.Fragment>
                ))}
              </div>
            </div>
          </div>
        ))}
      </div>
    ))}
  </div>
  );
};
const Dropdown1 = ({ label, items, type, isProjectDropdown, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);
  const [selectedCircles, setSelectedCircles] = useState({});
  
  const contentRef = useRef(null);
  const [scrollTop, setScrollTop] = useState(0);
  const [thumbHeight, setThumbHeight] = useState(40);
  const [isDragging, setIsDragging] = useState(false);
  const [startY, setStartY] = useState(0);
  const [startScrollTop, setStartScrollTop] = useState(0);
  const thumbRef = useRef(null);

  const toggleCircle = (item) => {
      setSelectedCircles((prev) => ({
          ...prev,
          [item]: !prev[item],
      }));
  };

  const handleSelect = (item) => {
      setSelectedItems(prev => {
          if (prev.includes(item)) {
              return prev.filter(i => i !== item);
          } else {
              return [...prev, item];
          }
      });
      
      if (typeof onChange === "function") {
          onChange(type, item);
      }
  };

  useEffect(() => {
      const content = contentRef.current;
      if (!content) return;

      const updateThumbHeight = () => {
          const { scrollHeight, clientHeight } = content;
          const visibleRatio = clientHeight / scrollHeight;
          setThumbHeight(Math.max(visibleRatio * clientHeight, 20));
      };

      updateThumbHeight();
      window.addEventListener("resize", updateThumbHeight);
      return () => window.removeEventListener("resize", updateThumbHeight);
  }, [isOpen, items]);

  const handleScroll = () => {
      const content = contentRef.current;
      if (!content) return;

      const { scrollTop, scrollHeight, clientHeight } = content;
      const maxScrollTop = scrollHeight - clientHeight;
      const scrollRatio = scrollTop / maxScrollTop;
      const trackHeight = content.clientHeight - thumbHeight;
      setScrollTop(scrollRatio * trackHeight);
  };

  const handleThumbMouseDown = (e) => {
      e.preventDefault();
      e.stopPropagation();

      setIsDragging(true);
      setStartY(e.clientY);
      setStartScrollTop(contentRef.current.scrollTop);

      document.addEventListener("mousemove", handleThumbMouseMove);
      document.addEventListener("mouseup", handleThumbMouseUp);
      document.body.style.cursor = "grabbing";
  };

  const handleThumbMouseMove = (e) => {
      if (!isDragging) return;

      const deltaY = e.clientY - startY;
      const content = contentRef.current;
      const { scrollHeight, clientHeight } = content;
      const maxScrollTop = scrollHeight - clientHeight;
      const trackHeight = clientHeight - thumbHeight;

      const scrollDelta = (deltaY / trackHeight) * maxScrollTop;
      const newScrollTop = Math.min(
          Math.max(0, startScrollTop + scrollDelta),
          maxScrollTop
      );

      content.scrollTop = newScrollTop;
  };

  const handleThumbMouseUp = () => {
      setIsDragging(false);
      document.removeEventListener("mousemove", handleThumbMouseMove);
      document.removeEventListener("mouseup", handleThumbMouseUp);
      document.body.style.cursor = "";
  };

  const handleTrackClick = (e) => {
      const content = contentRef.current;
      if (!content || e.target === thumbRef.current) return;

      const track = e.currentTarget;
      const trackRect = track.getBoundingClientRect();
      const clickPosition = e.clientY - trackRect.top;
      const thumbCenter = thumbHeight / 2;
      const scrollRatio =
          (clickPosition - thumbCenter) / (trackRect.height - thumbHeight);

      const { scrollHeight, clientHeight } = content;
      const maxScrollTop = scrollHeight - clientHeight;
      content.scrollTop = scrollRatio * maxScrollTop;
  };

  const handleButtonScroll = (direction) => {
      if (contentRef.current) {
          contentRef.current.scrollBy({
              top: direction === 'up' ? -50 : 50,
              behavior: 'smooth'
          });
      }
  };

  return (
      <div className="flex flex-col w-full relative">
          <span className="text-[6px] md:text-[20px] md:mb-3 font-extrabold">
              {isProjectDropdown ? "" : label || "LANGUAGE"}
          </span>
          <div className="flex relative z-20 w-fit">
              <button
                  onClick={() => {
                      if (isProjectDropdown && selectedItems.includes("Funding")) {
                          window.location.href = "/funding";
                      } else {
                          setIsOpen(prev => !prev);
                      }
                  }}
                  className={`xl:w-[160px] lg:w-[140px] md:w-[55px] w-[50px] h-[15px] md:h-[50px] bg-[#2E453D] text-white font-medium flex items-center px-1 rounded-l-[5px]`}
              >
                  <span className="md:text-[18px] md:px-2 text-[4px] md:mt-0 mt-[2px] truncate">
                      {selectedItems.length > 0 ? selectedItems[0] : "-"}
                  </span>
              </button>

              <div
                  className="w-[15px] h-[15px] md:h-[50px] md:w-[40px] bg-[#152C24] text-white flex justify-center items-center rounded-r-[5px] -ml-2 z-30 cursor-pointer"
                  onClick={() => setIsOpen(prev => !prev)}
              >
                  <img
                      src="https://res.cloudinary.com/dzngpgki4/image/upload/v1745014174/Expand_down_3_ggzyur.png"
                      alt="expand"
                      className="w-2 h-[10px] md:w-6 md:h-[24px]"
                  />
              </div>
          </div>

          <AnimatePresence>
              {isOpen && (
                  <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      className="absolute top-full mt-1 left-0 z-[100] md:w-[220px] w-[70px] border-[1px] border-[#979797]/80 bg-white rounded-[1px] shadow-lg p-2"
                  >
                      <div className="w-full flex justify-end py-1">
                          <button 
                              onClick={(e) => {
                                  e.stopPropagation();
                                  handleButtonScroll('up');
                              }}
                              className="p-1"
                          >
                              <img 
                                  src="https://res.cloudinary.com/dzngpgki4/image/upload/v1747461105/Expand_up_mflvtu.png" 
                                  alt="Scroll up" 
                                  className="w-3 h-3"
                              />
                          </button>
                      </div>

                      <div className="custom-scroll-area">
                          <ul
                              className="custom-scroll-content"
                              ref={contentRef}
                              onScroll={handleScroll}
                          >
                          <li className="flex items-center justify-between px-2 py-1 mb-2">
                              <span className="text-[6px] md:text-[20px] font-semibold">
                                  {isProjectDropdown ? "Purpose" : label || "Language"}
                              </span>
                              <img
                                  src="https://res.cloudinary.com/dzngpgki4/image/upload/v1750034880/Export_1_vurzim.png"
                                  alt="icon"
                                  className="md:w-[30px] md:h-[30px] h-[7px] w-[7px] cursor-pointer"
                                  onClick={(e) => {
                                      e.stopPropagation();
                                      setIsOpen(false);
                                  }}
                              />
                          </li>
                              
                              {items && items.map((item, index) => (
                                  <li
                                      key={index}
                                      className={`flex items-center justify-between md:px-3 px-1 md:py-2 py-1 md:mt-1 rounded-md hover:bg-gray-100 cursor-pointer ${
                                          selectedItems.includes(item) ? "bg-[#E7EBEB]" : ""
                                      }`}
                                      onClick={() => handleSelect(item)}
                                  >
                                      <span className="text-black text-[4px] md:text-[18px]">
                                          {item}
                                      </span>
                                      <div
                                          className={`w-2 h-2 md:w-8 md:h-8 rounded-full ${
                                              selectedItems.includes(item) ? "bg-[#152C24]" : "bg-gray-200"
                                          }`}
                                      ></div>
                                  </li>
                              ))}
                          </ul>
                          <div className="custom-scroll-track" onClick={handleTrackClick}>
                              <div
                                  ref={thumbRef}
                                  className="custom-scroll-thumb"
                                  style={{
                                      height: `${thumbHeight}px`,
                                      top: `${scrollTop}px`,
                                      cursor: isDragging ? "grabbing" : "grab",
                                  }}
                                  onMouseDown={handleThumbMouseDown}
                              ></div>
                          </div>
                      </div>

                      <div className="w-full flex justify-end py-1">
                          <button 
                              onClick={(e) => {
                                  e.stopPropagation();
                                  handleButtonScroll('down');
                              }}
                              className="p-1"
                          >
                              <img 
                                  src="https://res.cloudinary.com/dzngpgki4/image/upload/v1747461085/Expand_down_vbf4bb.png" 
                                  alt="Scroll down" 
                                  className="w-3 h-3"
                              />
                          </button>
                      </div>
                  </motion.div>
              )}
          </AnimatePresence>
          <p className="md:text-[14px] text-[4px] font-normal mt-1 text-black">
              {isProjectDropdown
                  ? "Select type of Produce"
                  : `Select  ${type === "FACILITATOR" ? "Weekly report" : label}`}
          </p>

          <style jsx>{`
              .custom-scroll-area {
                  position: relative;
                  max-height: 200px;
                  display: flex;
                  overflow: hidden;
              }

              .custom-scroll-content {
                  flex: 1;
                  overflow-y: scroll;
                  scrollbar-width: none;
                  -ms-overflow-style: none;
                  padding-right: 4px;
              }

              .custom-scroll-content::-webkit-scrollbar {
                  display: none;
              }

              .custom-scroll-track {
                  width: 14px;
                  background: #e7ebeb;
                  border-radius: 3px;
                  position: relative;
                  margin-left: 4px;
                  cursor: pointer;
              }

              .custom-scroll-thumb {
                  width: 100%;
                  background: #979797;
                  border-radius: 3px;
                  position: absolute;
                  right: 0;
                  transition: background 0.2s;
              }

              .custom-scroll-thumb:hover {
                  background: #444;
              }

              @media (max-width: 768px) {
                  .custom-scroll-track {
                      width: 4px;
                      margin-left: 2px;
                  }
                  .custom-scroll-area {
                      position: relative;
                      max-height: 150px;
                      display: flex;
                      overflow: hidden;
                      width: 100%;
                  }
              }
          `}</style>
      </div>
  );
};

const Dropdown = ({ items, selectedItem, onItemSelect }) => {
  const [isOpen, setIsOpen] = useState(false);
  const contentRef = useRef(null);
  const [scrollTop, setScrollTop] = useState(0);
  const [thumbHeight, setThumbHeight] = useState(40);
  const [isDragging, setIsDragging] = useState(false);
  const [startY, setStartY] = useState(0);
  const [startScrollTop, setStartScrollTop] = useState(0);
  const thumbRef = useRef(null);

  const toggleDropdown = () => setIsOpen(!isOpen);
  
  const handleItemClick = (item) => {
    onItemSelect(item);
    setIsOpen(false);
  };

  useEffect(() => {
    const content = contentRef.current;
    if (!content) return;

    const updateThumbHeight = () => {
      const { scrollHeight, clientHeight } = content;
      const visibleRatio = clientHeight / scrollHeight;
      setThumbHeight(Math.max(visibleRatio * clientHeight, 20));
    };

    updateThumbHeight();
    window.addEventListener("resize", updateThumbHeight);
    return () => window.removeEventListener("resize", updateThumbHeight);
  }, [isOpen, items]);

  const handleScroll = () => {
    const content = contentRef.current;
    if (!content) return;

    const { scrollTop, scrollHeight, clientHeight } = content;
    const maxScrollTop = scrollHeight - clientHeight;
    const scrollRatio = scrollTop / maxScrollTop;
    const trackHeight = content.clientHeight - thumbHeight;
    setScrollTop(scrollRatio * trackHeight);
  };
  const UnselectedIcon = () => (
    <svg
      viewBox="0 0 17 17"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className="w-[6px] h-[6px] md:w-[17px] md:h-[17px]"
    >
      <circle cx="8.5" cy="8.5" r="8" fill="#D9D9D9" stroke="none" />
    </svg>
  );
  
  const SelectedIcon = () => (
    <svg
      viewBox="0 0 17 17"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className="w-[6px] h-[6px] md:w-[17px] md:h-[17px]"
    >
      <circle cx="8.5" cy="8.5" r="8" fill="#2E453D" stroke="#FFBD26" />
      <path
        d="M5 8.5L7.5 11L12 6"
        stroke="white"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
  const handleThumbMouseDown = (e) => {
    e.preventDefault();
    e.stopPropagation();

    setIsDragging(true);
    setStartY(e.clientY);
    setStartScrollTop(contentRef.current.scrollTop);

    document.addEventListener("mousemove", handleThumbMouseMove);
    document.addEventListener("mouseup", handleThumbMouseUp);
    document.body.style.cursor = "grabbing";
  };

  const handleThumbMouseMove = (e) => {
    if (!isDragging) return;

    const deltaY = e.clientY - startY;
    const content = contentRef.current;
    const { scrollHeight, clientHeight } = content;
    const maxScrollTop = scrollHeight - clientHeight;
    const trackHeight = clientHeight - thumbHeight;

    const scrollDelta = (deltaY / trackHeight) * maxScrollTop;
    const newScrollTop = Math.min(
      Math.max(0, startScrollTop + scrollDelta),
      maxScrollTop
    );

    content.scrollTop = newScrollTop;
  };

  const handleThumbMouseUp = () => {
    setIsDragging(false);
    document.removeEventListener("mousemove", handleThumbMouseMove);
    document.removeEventListener("mouseup", handleThumbMouseUp);
    document.body.style.cursor = "";
  };

  const handleTrackClick = (e) => {
    const content = contentRef.current;
    if (!content || e.target === thumbRef.current) return;

    const track = e.currentTarget;
    const trackRect = track.getBoundingClientRect();
    const clickPosition = e.clientY - trackRect.top;
    const thumbCenter = thumbHeight / 2;
    const scrollRatio =
      (clickPosition - thumbCenter) / (trackRect.height - thumbHeight);

    const { scrollHeight, clientHeight } = content;
    const maxScrollTop = scrollHeight - clientHeight;
    content.scrollTop = scrollRatio * maxScrollTop;
  };

  const handleButtonScroll = (direction) => {
    if (contentRef.current) {
      contentRef.current.scrollBy({
        top: direction === 'up' ? -50 : 50,
        behavior: 'smooth'
      });
    }
  };

  return (
    <div className="relative mt-2">
      <button 
        className="w-full"
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        onClick={toggleDropdown}
      >
        <div className="flex items-center md:gap-2 gap-1">
          <p className="font-semibold md:text-[14px] text-[4px] text-[#152C24] mb-1">
            {selectedItem ? `${selectedItem.label} (${selectedItem.value})` : 'CONTRACTS'}
          </p>
          <img 
            src="https://res.cloudinary.com/dzngpgki4/image/upload/v1747676808/Expand_down_4_qe4gpr.png" 
            alt="Dropdown indicator" 
            className="md:w-[26px] md:h-[26px] h-[12px] w-[12px]"
          />
        </div>
      </button>
      
      {isOpen && (
        <div 
          className="absolute z-10 w-[80px] md:w-[200px] mt-1 bg-[#2E453D] rounded-md shadow-lg"
          role="listbox"
        >
          <div className="w-full flex justify-between items-center px-2 py-1">
            <button 
              onClick={(e) => {
                e.stopPropagation();
                handleButtonScroll('up');
              }}
              className="p-1"
            >
              <img 
                src="https://res.cloudinary.com/dzngpgki4/image/upload/v1747461105/Expand_up_mflvtu.png" 
                alt="Scroll up" 
                className="w-3 h-3"
              />
            </button>
            <img 
              src="https://res.cloudinary.com/dwfdzdaxk/image/upload/v1751835721/Export_emyg5v.png" 
              alt="Export" 
              className="w-4 h-4"
            />
          </div>

          <div className="custom-scroll-area">
            <div
              className="custom-scroll-content"
              ref={contentRef}
              onScroll={handleScroll}
            >
              {items.map((group) => (
                <div key={group.heading} className="mb-2">
                 
                  {group.items.map((item) => (
                    <div
                      key={item.value}
                      className={`px-3 py-2 hover:bg-[#03897B]/10 cursor-pointer ${
                        selectedItem?.value === item.value ? 'bg-[#03897B]/10' : ''
                      }`}
                      role="option"
                      onClick={() => handleItemClick(item)}
                    >
                      <div className="flex justify-between items-center">
                        <div className="md:text-[14px] text-[6px] font-medium text-white">
                          {item.label}  
                        </div>
                        <div className="ml-2">
                          {selectedItem?.value === item.value ? (
                            <SelectedIcon />
                          ) : (
                            <UnselectedIcon />
                          )}
                        </div>
                      </div>
                      <div className="md:text-[11px] text-[4px] text-gray-300">
                        {item.value}
                      </div>
                    </div>
                  ))}
                </div>
              ))}
            </div>
            <div className="custom-scroll-track" onClick={handleTrackClick}>
              <div
                ref={thumbRef}
                className="custom-scroll-thumb"
                style={{
                  height: `${thumbHeight}px`,
                  top: `${scrollTop}px`,
                  cursor: isDragging ? "grabbing" : "grab",
                }}
                onMouseDown={handleThumbMouseDown}
              ></div>
            </div>
          </div>

          <div className="w-full flex justify-end py-1">
            <button 
              onClick={(e) => {
                e.stopPropagation();
                handleButtonScroll('down');
              }}
              className="p-1"
            >
              <img 
                src="https://res.cloudinary.com/dzngpgki4/image/upload/v1747461085/Expand_down_vbf4bb.png" 
                alt="Scroll down" 
                className="w-3 h-3"
              />
            </button>
          </div>
        </div>
      )}

      <style jsx>{`
        .custom-scroll-area {
          position: relative;
          max-height: 200px;
          display: flex;
          overflow: hidden;
        }

        .custom-scroll-content {
          flex: 1;
          overflow-y: scroll;
          scrollbar-width: none;
          -ms-overflow-style: none;
          padding-right: 4px;
        }

        .custom-scroll-content::-webkit-scrollbar {
          display: none;
        }

        .custom-scroll-track {
          width: 14px;
          background: #e7ebeb;
          border-radius: 3px;
          position: relative;
          margin-left: 4px;
          cursor: pointer;
        }

        .custom-scroll-thumb {
          width: 100%;
          background: #979797;
          border-radius: 3px;
          position: absolute;
          right: 0;
          transition: background 0.2s;
        }

        .custom-scroll-thumb:hover {
          background: #444;
        }

        @media (max-width: 768px) {
          .custom-scroll-track {
            width: 8px;
            margin-left: 2px;
          }
          .custom-scroll-area {
            position: relative;
            max-height: 150px;
            display: flex;
            overflow: hidden;
            width: 100%;
          }
        }
      `}</style>
    </div>
  );
};

const Reports = () => {
  const handleDateClick = (dateType) => {
    setDatePickerFor(dateType);
    setShowDatePicker(true);
  };
  const challengesData = [
    {
      id: 1,
      title: "Limited Access to Finance",
      code: "ACCESS TO FINANCE",
      description: "Many farmers lack access to affordable credit and insurance.",
      solution: "Solution: Develop inclusive agricultural financing, microloans, and mobile banking services tailored to smallholders."
    },
    {
      id: 2,
      title: "Poor Infrastructure",
      code: "COMPLIANCE",
      description: "Inadequate roads, storage, and market facilities hinder distribution and increase post-harvest losses.",
      solution: "Solution: Invest in rural infrastructure, including roads, warehouses, and market access points."
    },
    {
      id: 3,
      title: "Low Access to Technology and Inputs",
      code: "MARKET ACCESS",
      description: "Limited availability of quality seeds, fertilizers, and modern tools.",
      solution: "Solution: Encourage public-private partnerships to improve input supply chains and subsidize access for small-scale farmers."
    },
    {
      id: 4,
      title: "Limited Agricultural Extension Services",
      code: "SKILL GAPS",
      description: "Farmers often lack access to updated farming knowledge and practices.",
      solution: "Solution: Strengthen extension services using mobile platforms, local training centers, and demonstration farms."
    },
    {
      id: 5,
      title: "Market Access and Price Volatility",
      code: "TECHNOLOGY",
      description: "Farmers struggle to connect with profitable markets and face unstable prices.",
      solution: "Solution: Support farmer cooperatives, develop value chains, and use digital platforms to connect farmers with buyers."
    },
    {
      id: 6,
      title: "Climate Change & Unpredictable Weather",
      code: "CASH FLOW",
      description: "Droughts, floods, and changing weather patterns disrupt farming cycles.",
      solution: "Solution: Promote climate-smart agriculture, drought-resistant seeds, and improved weather forecasting tools."
    },
    {
      id: 7,
      title: "Youth Disinterest in Agriculture",
      code: "MENTORSHIP",
      description: "Agriculture is seen as unprofitable and labor-intensive.",
      solution: "Solution: Promote agribusiness, use of technology, and access to land and finance for young entrepreneurs."
    },
    {
      id: 8,
      title: "Pests and Diseases",
      code: "INSTABILITY",
      description: "Crop and livestock pests can wipe out harvests and income.",
      solution: "Solution: Increase research on pest control, and provide early warning systems and access to effective pesticides or biological controls."
    },
    {
      id: 9,
      title: "Land Tenure Insecurity",
      code: "INNOVATION",
      description: "Many farmers work without formal land rights, discouraging long-term investment.",
      solution: "Solution: Implement land reforms and promote secure, documented land ownership."
    },
    {
      id: 10,
      title: "Lack of Skills and Technical Know-How",
      code: "INFORMATION ACCESS",
      description: "Limits smallholder farmers' productivity and income potential by preventing them from adopting modern, efficient farming practices.",
      solution: "Solution: Targeted training, access to extension services, and digital tools tailored to their needs."
    },
    {
      id: 11,
      title: "Crime and Insecurity",
      code: "SECURITY",
      description: "Disrupt smallholder farmers' activities by threatening their safety, crops, and livestock, leading to reduced productivity and investment.",
      solution: "Solution: Strengthened rural security, community policing, and support systems to protect livelihoods."
    },
    {
      id: 12,
      title: "Lack of Information",
      code: "INFORMATION ACCESS",
      description: "Prevents smallholder farmers from making informed decisions about markets, weather, and best practices, leading to lower yields and income.",
      solution: "Solution: Improved access to timely, relevant agricultural information through mobile platforms and extension services."
    },
    {
      id: 13,
      title: "Others",
      code: "OTHERS",
      description: "",
      solution: ""
    }
  ];
  
  const [isHovered, setIsHovered] = useState(false);
  const [dropdownSelections, setDropdownSelections] = useState({
    user_type: "English", // Default to English
    user_purpose: "View reports",
    user_category: "All categories"
  });
  
  const [hoveredItems, setHoveredItems] = useState({});
  const [isOpen, setIsOpen] = useState(false);
  const [selected, setSelected] = useState('');
  const [regions, setRegions] = useState([]);
  const regionDisplayNames = regions;
  const toggleDropdown = () => setIsOpen(!isOpen);
  const [filteredTotal, setFilteredTotal] = useState(0);
  const [selectedItem, setSelectedItem] = useState(null);
  const [agreed, setAgreed] = useState(false);
  const handleItemClick = (value) => {
    setSelected(value);
    setIsOpen(false);
  };
  const { selectedCountry } = useCountry();

  const currentCountry = selectedCountry?.name || 'South Africa';
 
  const [enterpriseTotal, setEnterpriseTotal] = useState(0);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [datePickerFor, setDatePickerFor] = useState(null);
  const [openingDate, setOpeningDate] = useState('');
  const [closingDate, setClosingDate] = useState('');
  const [challengeStats, setChallengeStats] = useState({
    'ACCESS TO FINANCE': 0,
    'COMPLIANCE': 0,
    'MARKET ACCESS': 0,
    'SKILL GAPS': 0,
    'TECHNOLOGY': 0,
    'CASH FLOW': 0,
    'MENTORSHIP': 0,
    'INSTABILITY': 0,
    'INNOVATION': 0,
    'INFORMATION ACCESS': 0,
    'SECURITY': 0,
    'OTHERS': 0
  });

  const [reportSelections, setReportSelections] = useState({
    COUNTRY: "-",
    REGION: "-",
    DISTRICT: "-",
    MUNICIPALITY: "-",
    SMMEs: "-",
    "VALUE CHAIN": "-",
    PROJECT: "-",
    "USER TYPE": "-"
  });
  
  const [totalEntrepreneurs, setTotalEntrepreneurs] = useState(0);

  const handleReportDropdownChange = (type, value) => {
    setLastChangedDropdown(type); // Track which dropdown was changed
    setReportSelections(prev => {
      const newSelections = {
        ...prev,
        [type]: value
      };
      
      if (type === 'COUNTRY') {
        newSelections.REGION = "-";
        newSelections.DISTRICT = "-";
        newSelections.MUNICIPALITY = "-";
      }
      
      return newSelections;
    });
  };
  

  const handleSaveFromDropdown = (savedData) => {
    setReportSelections(savedData);
  };

  const handleDateApply = ({ date }) => {
    if (datePickerFor === 'opening') {
      setOpeningDate(date);
    } else {
      setClosingDate(date);
    }
  }; 
  
  const totalSelections = Object.values(challengeStats).reduce((sum, val) => sum + val, 0);
  
  const calculateNormalizedPercentages = () => {
    const countryTotal = totalEntrepreneurs || 1;
    
    const challengesWithData = challengesData.map(challenge => {
      const count = challengeStats[challenge.code] || 0;
      const percentage = Math.round((count / countryTotal) * 1);
      
      return {
        ...challenge,
        value: count,
        percentage: `${percentage}%`,
        rawPercentage: percentage
      };
    });

    const maxValue = Math.max(...challengesWithData.map(c => c.value));
    
    return {
      normalizedChallenges: challengesWithData.map(item => ({
        ...item,
        isHighest: item.value === maxValue && item.value > 0
      })),
      accessToFinancePercentage: challengesWithData.find(c => c.code === "ACCESS TO FINANCE")?.percentage || "0%"
    };
  }; 
  
  const { normalizedChallenges, accessToFinancePercentage } = calculateNormalizedPercentages();

  const handleDropdownChange = (type, value) => {
    setDropdownSelections(prev => ({
      ...prev,
      [type]: value
    }));
  };

  const countryMapImages = {
    'Angola': 'https://res.cloudinary.com/dzngpgki4/image/upload/v1750444884/Angola_Map_qri8d9.jpg',
    'Botswana': 'https://res.cloudinary.com/dzngpgki4/image/upload/v1750444904/Botswana_Map_ixujke.jpg',
    'Cameroon': 'https://res.cloudinary.com/dzngpgki4/image/upload/v1750444883/Cameroon_Map_oli1ea.jpg',
    'Côte d\'Ivoire (Ivory Coast)': 'https://res.cloudinary.com/dzngpgki4/image/upload/v1750444885/Cote_D_Ivorie_Map_titbtu.jpg',
    'Congo (Kinshasa/DRC)': 'https://res.cloudinary.com/dzngpgki4/image/upload/v1750444894/Congo_DRC_map_eooyiv.jpg',
    'Eswatini': 'https://res.cloudinary.com/dzngpgki4/image/upload/v1750444890/Eswatini_Map_qg49ik.jpg',
    'Ethiopia': 'https://res.cloudinary.com/dzngpgki4/image/upload/v1750444894/Ethiopia_map_ijxizh.jpg',
    'Ghana': 'https://res.cloudinary.com/dzngpgki4/image/upload/v1750444895/Ghana_Map_at9tji.jpg',
    'Kenya': 'https://res.cloudinary.com/dzngpgki4/image/upload/v1750444894/Kenya_Map_axuptr.jpg',
    'Lesotho': 'https://res.cloudinary.com/dzngpgki4/image/upload/v1750444894/Lesotho_Map_wwweao.jpg',
    'Malawi': 'https://res.cloudinary.com/dzngpgki4/image/upload/v1750444895/Malawi_Map_cezzt0.jpg',
    'Mozambique': 'https://res.cloudinary.com/dzngpgki4/image/upload/v1750444889/Mozambique_map_mr5gti.jpg',
    'Namibia': 'https://res.cloudinary.com/dzngpgki4/image/upload/v1750444889/Namibia_Map_i2r8th.jpg',
    'Nigeria': 'https://res.cloudinary.com/dzngpgki4/image/upload/v1750444890/Nigeria_Map_fyxypv.jpg',
    'Rwanda': 'https://res.cloudinary.com/dzngpgki4/image/upload/v1750447501/Rwanda_Map_dslxiy.jpg',
    'Senegal': 'https://res.cloudinary.com/dzngpgki4/image/upload/v1750444882/Senegal_Map_gbxrbb.jpg',
    'Somalia': 'https://res.cloudinary.com/dzngpgki4/image/upload/v1750444942/Somalia_Map_xwxy02.jpg',
    'South Africa': 'https://res.cloudinary.com/dzngpgki4/image/upload/v1750444882/South_Africa_map_fhljpi.jpg',
    'South Sudan': 'https://res.cloudinary.com/dzngpgki4/image/upload/v1750444882/South_Sudan_Map_ox0wyh.jpg',
    'Tanzania': 'https://res.cloudinary.com/dzngpgki4/image/upload/v1750447501/Tanzania_map_pwlaay.jpg',
    'Uganda': 'https://res.cloudinary.com/dzngpgki4/image/upload/v1750444883/Uganda_map_rrjk9p.jpg',
    'Zambia': 'https://res.cloudinary.com/dzngpgki4/image/upload/v1750444885/Zambia_Map_izofpt.jpg'
  };

  useEffect(() => {
    const fetchChallengeStats = async () => {
      try {
        const statsRef = doc(db, "stats", "challenges");
        const statsDoc = await getDoc(statsRef);
        
        if (statsDoc.exists()) {
          const allStats = statsDoc.data();
          const countryStats = allStats[selectedCountry.name] || {};
          
          const initializedStats = {};
          challengesData.forEach(challenge => {
            initializedStats[challenge.code] = countryStats[challenge.code] || 0;
          });
          
          setChallengeStats(initializedStats);
        } else {
          const q = query(
            collectionGroup(db, "challengeSubmissions"),
            where("country", "==", selectedCountry.name)
          );
          
          const querySnapshot = await getDocs(q);
          const calculatedStats = {};
          
          challengesData.forEach(challenge => {
            calculatedStats[challenge.code] = 0;
          });
          
          querySnapshot.forEach((doc) => {
            const submission = doc.data();
            submission.challenges.forEach(challengeId => {
              const challenge = challengesData.find(c => c.id === challengeId);
              if (challenge) {
                calculatedStats[challenge.code] += 1;
              }
            });
          });
          
          setChallengeStats(calculatedStats);
        }
      } catch (error) {
        console.error("Error fetching challenge stats:", error);
      }
    };
    
    fetchChallengeStats();
  }, [selectedCountry]);

  const updateStats = async (country, region, smmeType, landSize) => {
    const statsRef = doc(db, "stats", "registrations");
    
    try {
      await runTransaction(db, async (transaction) => {
        const statsDoc = await transaction.get(statsRef);
        let statsData = statsDoc.exists() ? statsDoc.data() : {};
        
        if (!statsData[country]) {
          statsData[country] = {};
        }
        
        if (!statsData[country][region]) {
          statsData[country][region] = {
            total: 0,
            landSizeTotal: 0,
            bySmmetype: {}
          };
        }
        
        if (!statsData[country][region].bySmmetype[smmeType]) {
          statsData[country][region].bySmmetype[smmeType] = {
            count: 0,
            landSizeTotal: 0
          };
        }
        
        statsData[country][region].total += 1;
        statsData[country][region].landSizeTotal += landSize;
        statsData[country][region].bySmmetype[smmeType].count += 1;
        statsData[country][region].bySmmetype[smmeType].landSizeTotal += landSize;
        
        transaction.set(statsRef, statsData);
      });
    } catch (error) {
      console.error("Transaction failed:", error);
    }
  };

  const renderButtonWithIcon = (text, bgColor, iconColor, isLarge = false) => {
    const bgColorClasses = {
      "#152C24": "bg-[#152C24]",
      "#2E453D": "bg-[#2E453D]",
      "#FDBB26": "bg-[#FDBB26]",
    };

    const iconColorClasses = {
      "#152C24": "bg-[#152C24]",
      "#2E453D": "bg-[#2E453D]",
      "#FDBB26": "bg-[#FDBB26]",
    };

    return (
      <div className="flex items-center">
        <div className={`${bgColorClasses[bgColor]} text-white md:text-[16px] text-[5px] px-2 md:px-4 ${
          isLarge ? "md:py-[14px] py-[3px]" : "md:py-[14px] py-[3px]"
        } md:rounded-l-md rounded-l-md font-bold md:w-[300px] whitespace-nowrap flex items-center h-full`}>
          {text}
        </div>
        
        <div className={`${iconColorClasses[iconColor]} hidden md:flex items-center md:py-2  justify-center md:w-[57px] md:h-[52px] rounded-r-md`}>
          <img
            src="https://res.cloudinary.com/dzngpgki4/image/upload/v1744139834/Expand_right_2_a4wodr.png"
            alt="Expand"
            className="w-[20px] h-[6px] md:h-[40px] md:w-[55px]  object-contain"
          />
        </div>
        
        <div className={`${iconColorClasses[iconColor]} md:hidden flex items-center justify-center w-[20px]  h-[14px] rounded-r-md`}>
          <img
            src="https://res.cloudinary.com/dzngpgki4/image/upload/v1744370865/Expand_right_5_l2cjmf.png"
            alt="Expand"
            className="w-[20px] h-[6px]  object-contain"
          />
        </div>
      </div>
    );
  };

  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  
  useEffect(() => {
    console.log('Total entrepreneurs updated:', totalEntrepreneurs);
  }, [totalEntrepreneurs]);

  // Define dropdown options
  const languageOptions = [
   "01", "02", "03", "04", "05", "06", "07", "08", "09", "10", "11", "12", "13", "14", "15", "16", "17", "18", "19", "20", "21", "22", "23", "24", "25", "26", "27", "28", "29", "30", "31"
  ];

  const purposeOptions = [
    "January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December", 
  ];

  const categoryOptions = [
  "2025","2024", "2023", "2022", "2021", "2020", "2019", "2018", "2017", "2016", "2015", "2014", "2013", "2012", "2011", "2010", "2009", "2008", "2007", "2006", "2005"
  ];
  const dropdownItems = [
    {
      heading: "Agriculture and Agribusiness",
      items: [
        { label: "Smallholder farms", value: "SMF" },
        { label: "Agro-processing", value: "AGP" },
        { label: "Agri-input suppliers", value: "AIP" },
        { label: "Horticulture businesses", value: "HCB" },
        { label: "Poultry", value: "POF" },
        { label: "Fish farms", value: "FIF" },
        { label: "Agri-tech startups", value: "ATC" }
      ]
    },
    {
      heading: "Manufacturing and Processing",
      items: [
        { label: "Textile and garment production", value: "TGP" },
        { label: "Furniture making and carpentry", value: "FPC" },
        { label: "Metal fabrication", value: "MEF" },
        { label: "Soap and detergent production", value: "SDP" },
        { label: "Food and beverage processing", value: "FBP" }
      ]
    },
    {
      heading: "Retail and Trade",
      items: [
        { label: "Small shops (dukas/spazas)", value: "SDS" },
        { label: "Market vendors", value: "MAV" },
        { label: "Wholesalers and distributors", value: "WHD" },
        { label: "Online retail platforms", value: "ORP" }
      ]
    },
    {
      heading: "Services",
      items: [
        { label: "Transport and logistics", value: "TRL" },
        { label: "Repair services", value: "RPS" },
        { label: "Beauty and personal care", value: "BPC" },
        { label: "Laundry and dry cleaning", value: "LDC" },
        { label: "Hospitality", value: "HOS" },
        { label: "Event planning and catering", value: "EPC" }
      ]
    },
    {
      heading: "Technology and Innovation (Tech SMEs)",
      items: [
        { label: "Fintech", value: "FIT" },
        { label: "Healthtech", value: "HET" },
        { label: "Edtech", value: "EDT" },
        { label: "Software and app development", value: "SAD" },
        { label: "E-commerce and logistics tech", value: "ECL" }
      ]
    },
    {
      heading: "Construction and Real Estate",
      items: [
        { label: "Small construction firms", value: "SCF" },
        { label: "Real estate agents and developers", value: "RAD" },
        { label: "Hardware and building material suppliers", value: "HBM" }
      ]
    },
    {
      heading: "Creative and Cultural Industries",
      items: [
        { label: "Art and crafts production", value: "ACP" },
        { label: "Music and film production", value: "MFP" },
        { label: "Fashion and design businesses", value: "FDB" },
        { label: "Photography and videography services", value: "PVS" }
      ]
    },
    {
      heading: "Health and Wellness",
      items: [
        { label: "Private clinics and pharmacies", value: "PCP" },
        { label: "Fitness centers and gyms", value: "FCG" },
        { label: "Traditional medicine practitioners", value: "TMP" },
        { label: "Health product distributors", value: "HPD" }
      ]
    },
    {
      heading: "Education and Training",
      items: [
        { label: "Private schools and daycares", value: "PSD" },
        { label: "Vocational training centers", value: "VTC" },
        { label: "Tutoring services", value: "TUS" },
        { label: "Edtech startups", value: "EDS" }
      ]
    }
  ]
  const formatRegionName = (region) => {
    if (!region || region === "-") return "";
    if (typeof region === 'object') return region.name;
    return region.replace(/\b\w/g, char => char.toUpperCase());
  };
  const [lastChangedDropdown, setLastChangedDropdown] = useState('COUNTRY');

  // Helper function to get display text for each dropdown type
  const getDropdownDisplayText = (type, value) => {
    if (value === "-" || value === "") {
      return `All ${type === 'SMMEs' ? 'Types' : type === 'TYPE OF FARMING' ? 'Chains' : type + 's'}`;
    }
  
    if (typeof value === 'object') {
      if (type === 'SMMEs') {
        return value.label || value.value || value.name;
      }
      return value.name || value.value || value.label;
    }
    if (typeof value === 'object') {
      if (type === 'TYPE OF FARMING') {
        return value.heading || value.heading || value.heading;
      }
      return value.heading || value.heading || value.heading;
    }
    return value;
  };
  
  return (
    <div className='bg-[#F3F3F3] md:pt-5 pt-2'>
      <div className=''>
        <div className="md:px-24 px-4">
          <div className="flex justify-between items-start md:mt-5 mt-2">
            <div>
              <h2 className="md:text-[36px] text-[12px] capitalize text-[#152C24] font-bold">
                <span className="text-[#979797] capitalize">Reports | </span>{selectedCountry?.name || 'South Africa'}
              </h2>
            </div>
            <div>
                <p className="md:text-[20px] text-[4px] font-bold mt-1">LOG IN</p>
                <div className="flex items-center flex-shrink-0 md:mt-0 md:pr-0 pr-3 ">
                  <input 
                    className="md:text-[16px] text-[5px] placeholder-white border-[#2E453D] outline-none bg-[#2E453D] text-white md:px-4 px-2 md:w-[300px] md:py-[16px] py-[5px] md:rounded-tl-md rounded-tl-md font-bold md:rounded-bl-md rounded-bl-md whitespace-nowrap h-full" 
                    placeholder="XXXXXXXXXX"
                  />
                
                </div>
                <p className="md:text-[14px] text-[4px] font-normal mt-1">Insert user Id number to view reports</p>
              </div>
          </div>

       
          <ReportsSection
        values={reportSelections}
        onChange={handleReportDropdownChange}
        smmesOptions={SMMEsOptions}
      />
          <div className="w-full md:mb-4 md:mt-6 mb-2">
            <div className="relative">
              <input
                placeholder="Search"
                type="text"
                className="w-full md:h-[61px] h-[30px] text-[#979797] text-[10px] md:text-[16px] font-semibold md:p-3 pr-10 p-1 md:px-6 px-3 border border-[#C4C4C4] md:rounded-md rounded-full focus:outline-none focus:ring-2 focus:ring-green-600"
              />
              <img
                src="https://res.cloudinary.com/dzngpgki4/image/upload/v1744490602/Search_k7vxkr.png"
                alt="Search Icon"
                className="w-[18px] h-[15px] md:h-[24px] md:w-[24px] absolute right-5 top-1/2 transform -translate-y-1/2"
              />
            </div>
          </div>
          
          

<div className="md:mt-5 mt-3 flex justify-between items-center">
<h2 className="md:text-[28px] text-[12px] text-[#152C24] font-bold">
    <span className="text-[#979797]">
      {lastChangedDropdown === 'SMMEs' ? 'CONTRACT' : lastChangedDropdown} | 
    </span>
 {getDropdownDisplayText(lastChangedDropdown, reportSelections[lastChangedDropdown])}
  </h2>
  
  <div className="flex justify-end md:mb-0 mb-3 md:mt-4">
    <div className="md:w-[316px] w-[80px] h-[20px] md:h-[51px] bg-[#2E453D] rounded-[5px] flex justify-end">
      <button className="md:w-[150px] w-[50px] h-full bg-[#2E453D] text-white text-[8px] md:text-[20px] font-bold rounded-[5px] z-10">
        Farmers
      </button>
      <button className="md:w-[150px] w-[50px] text-[8px] font-bold md:text-[20px] h-full bg-[#152C24] text-white rounded-[5px] z-20">
        {enterpriseTotal.toLocaleString()}
      </button>
    </div>
  </div>
</div>

          <h1 className="font-bold text-[8px] md:text-[20px] text-[#152C24] md:mt-14 mt-5">
            SURVEY ABOUT CHALLENGES OF ENTERPRENEURSHIP
          </h1>

          <div className="flex flex-col md:flex-row justify-between items-start md:mt-10 mt-4 gap-2">
            <div>
              <h1 className="font-bold text-[8px] md:text-[20px] md:w-[190px] text-[#152C24] md:mb-8">Challenges</h1>
              <h1 className="font-bold text-[8px] md:text-[20px] md:w-[190px] text-[#152C24] md:mb-8">--</h1>
              <h1 className="font-bold text-[8px] md:text-[20px] md:w-[190px] text-[#152C24]">--</h1>
            </div>
            <div className="grid grid-cols-5 md:grid-cols-5 gap-[2px] md:gap-[3px] w-full">
              {normalizedChallenges.map((item, index) => {
                const isHighest = item.value === Math.max(...normalizedChallenges.map(c => c.value));
              
                return (
                  <div 
                    key={index} 
                    className="relative"
                    onMouseEnter={() => setHoveredItems(prev => ({...prev, [index]: true}))}
                    onMouseLeave={() => setHoveredItems(prev => ({...prev, [index]: false}))}
                  >
                    <div className="flex flex-col">
                      <div className="flex items-center w-full mb-1">
                        <div 
                          className={`w-[50px] md:w-[170px] bg-[#CFD7D7] text-black font-normal flex md:px-2 px-1 items-center rounded-tl-[5px] h-[20px] md:h-[45px] ${
                            isHighest ? 'md:border-b-[4px] border-b-[2px] border-[#FDBB26]' : ''
                          }`}
                        >
                          <span className="text-[4px] md:text-[12px] w-full">{item.title}</span>
                        </div>
                        <div 
                          className={`w-[20px] md:w-[60px] bg-[#152C24] text-white flex items-center justify-center rounded-tr-[5px] md:px-2 px-1 h-[20px] md:h-[45px] ${
                            isHighest ? 'md:border-b-[4px] border-b-[2px]  border-[#FDBB26]' : ''
                          }`}
                        >
                          <span className="text-[4px] md:text-[12px]">{item.percentage}</span>
                        </div>
                      </div>
                      
                      <div className={`
                        w-full bg-[#E7EBEB] rounded-b-[5px]
                        transition-all duration-300 ease-in-out
                        overflow-hidden text-black
                        ${hoveredItems[index] ? 'max-h-[300px] py-5 px-2' : 'max-h-0 py-0 px-2'}
                      `}>
                        <p className="text-[4px] text-black md:text-[12px]">{item.description}</p>
                        <p className="text-[4px] text-black md:text-[12px]">{item.solution}</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          
          <div className="md:mt-5 mt-3 bg-white md:p-4 p-2 md:h-[170px] flex items-center justify-between">
            <div className="md:text-[20px] text-[8px] text-black">
              <div className="flex items-center md:gap-2 gap-1">
                <p className="font-bold">
                  {normalizedChallenges.find(c => c.isHighest)?.title || "Limited Access to Finance"}
                </p>
              </div>
              <ul className="md:mt-4">
                {normalizedChallenges.find(c => c.isHighest)?.description || "Many farmers lack access to affordable credit and insurance."}
                <br />
                {normalizedChallenges.find(c => c.isHighest)?.solution || "Solution: Develop inclusive agricultural financing, microloans, and mobile banking services tailored to smallholders"}
              </ul>
            </div>
            <div>
              <div className="bg-[#152C24] md:w-[200px] h-[40px] md:h-[95px] w-[50px] rounded-[5px] flex items-center justify-center md:mt-4 mt-2">
                <h1 className="md:text-[36px] text-[8px] font-bold text-white">
                  {normalizedChallenges.find(c => c.isHighest)?.percentage || accessToFinancePercentage}
                </h1>
              </div>
            </div>
          </div>
          
          <div className='md:h-[2px] h-[1px] bg-[#979797]'></div>
        </div>

        <div>
        <div >
 <div className="md:px-24 px-4">
 <div className="flex justify-between items-center">
    <h2 className="md:text-[36px] text-[10px] text-[#152C24] font-bold">
      <span className="text-[#979797]">AGGREGATOR | </span>ALL
    </h2>
    <img src="https://res.cloudinary.com/dzngpgki4/image/upload/v1751800571/image_1814_z9vymo.png" alt="/" className="w-[40px] h-[40px] md:w-[128px] md:h-[128px]"/>
  </div>
  
  {/* Three dropdowns tightly packed on the right */}
  <div className="flex justify-between">
<div className="flex md:gap-4 gap-2 items-center">
<div className="flex md:space-x-4 space-x-1 items-center cursor-pointer" 
    onClick={() => handleDateClick('opening')}>
    <h1 className="md:text-[20px] text-[6px] font-semibold">
      Opening Date {openingDate && `: ${openingDate}`}
    </h1>
    <img 
      src="https://res.cloudinary.com/dzngpgki4/image/upload/v1745016720/image_1686_xak1hv.png" 
      alt="calendar" 
      className="md:w-[22px] md:h-[22px] h-[10px]" 
    />
  </div>
  <div className="flex md:space-x-4 space-x-1 items-center cursor-pointer"
    onClick={() => handleDateClick('closing')}>
    <h1 className="md:text-[20px] text-[6px] font-semibold">
      Closing Date {closingDate && `: ${closingDate}`}
    </h1>
    <img 
      src="https://res.cloudinary.com/dzngpgki4/image/upload/v1745016720/image_1686_xak1hv.png" 
      alt="calendar" 
      className="md:w-[22px] md:h-[22px] h-[10px]" 
    />
  </div>

</div>
  {showDatePicker && (
    <DateRangePopup
      onClose={() => setShowDatePicker(false)}
      onApply={handleDateApply}
      dateType={datePickerFor}
    />
  )}


    <div className="flex gap-2">
      <Dropdown1
        label="WEEKLY"
        items={languageOptions}
        type="FACILITATOR"
        isProjectDropdown={false}
        onChange={(type, value) => handleDropdownChange('user_type', value)}
        className="border-r-0 rounded-r-none"
      />
      
      <Dropdown1
        label="MONTHLY"
        items={purposeOptions}
        type="PURPOSE"
        isProjectDropdown={false}
        onChange={(type, value) => handleDropdownChange('user_purpose', value)}
        className="border-l-0 border-r-0 rounded-none"
      />
      
      <Dropdown1
        label="YEARLY"
        items={categoryOptions}
        type="CATEGORY"
        isProjectDropdown={false}
        onChange={(type, value) => handleDropdownChange('user_category', value)}
        className="border-l-0 rounded-l-none"
      />
    </div>
  </div>
  
 </div>
  <div className="md:flex hidden">
    <Chart/>
  </div>
</div>
          <div className="md:hidden flex">
            <ChartMobile/>
          </div>
          
          <div className="md:px-24 px-4  md:-mt-40">
      <div className="md:flex hidden">
      <EnterpriseSection 
              regions={Object.keys(getRegionMap(reportSelections.COUNTRY))} 
              country={reportSelections.COUNTRY}
              reportSelections={reportSelections}
              onTotalCalculated={setEnterpriseTotal}
            />
      </div>
            <div className="md:hidden flex">
            <EnterpriseSection1 
              regions={Object.keys(getRegionMap(reportSelections.COUNTRY))} 
              country={reportSelections.COUNTRY}
              reportSelections={reportSelections}
              onTotalCalculated={setEnterpriseTotal}
            />
            </div>
            <div className="mb-3 md:mt-6 mt-3">
              <Dropdown 
                items={dropdownItems} 
                selectedItem={selectedItem} 
                onItemSelect={setSelectedItem} 
              />
              
              <div className='md:h-[2.5px] h-[1px] bg-black/50'></div>
              
              <div className="gap-y-1 md:gap-x-2 gap-x-1 md:mt-3 mt-1 items-center">
                <h1 className="md:text-[14px] text-[4px] text-black">
                  Select type of enterprise and see mapping of their locations
                </h1>
              </div>
            </div>
            
            <div className="relative w-full">
              <img
                src={
                  countryMapImages[reportSelections.COUNTRY] || 
                  countryMapImages[selectedCountry?.name] || 
                  "https://res.cloudinary.com/dzngpgki4/image/upload/v1750444882/South_Africa_map_fhljpi.jpg"
                }
                alt="Map"
                className="w-full h-full"
              />

              <img
                src="https://res.cloudinary.com/dzngpgki4/image/upload/v1747680890/image_1754_i5w9rd.png"
                alt="Map Icon"
                className="absolute top-[8%] left-[20%] w-5 md:w-28"
              />
              <img
                src="https://res.cloudinary.com/dzngpgki4/image/upload/v1747680890/image_1754_i5w9rd.png"
                alt="Map Icon"
                className="absolute top-[16%] left-[42%] w-5 md:w-28"
              />
              <img
                src="https://res.cloudinary.com/dzngpgki4/image/upload/v1747680890/image_1754_i5w9rd.png"
                alt="Map Icon"
                className="absolute top-[26%] right-[34%] w-5 md:w-28"
              />
              <img
                src="https://res.cloudinary.com/dzngpgki4/image/upload/v1747680890/image_1754_i5w9rd.png"
                alt="Map Icon"
                className="absolute top-[46%] left-[32%] w-5 md:w-28"
              />
              <img
                src="https://res.cloudinary.com/dzngpgki4/image/upload/v1747680890/image_1754_i5w9rd.png"
                alt="Map Icon"
                className="absolute top-[54%] left-[16%] w-5 md:w-28"
              />
            </div>
            
            <div className="flex gap-4 items-center mt-5">
              <div
                className="md:w-16 md:h-[63px] w-6 h-[30px] rounded-[5px] bg-[#D9D9D9] flex items-center justify-center cursor-pointer transition"
                onClick={() => setAgreed(!agreed)}
                tabIndex={0}
                aria-pressed={agreed}
                aria-label="Confirm terms and conditions"
              >
                {agreed ? (
                  <svg
                    width="54"
                    height="54"
                    viewBox="0 0 54 54"
                    fill="none"
                    className="md:w-[54px] md:h-[54px] w-[20px] h-[20px] transition-all duration-150"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <circle cx="27" cy="27" r="27" fill="#01897B" />
                    <path
                      d="M39 21L24.75 35L15 25.4545"
                      stroke="white"
                      strokeWidth="4"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                ) : (
                  <svg
                    width="54"
                    height="54"
                    viewBox="0 0 54 54"
                    fill="none"
                    className="md:w-[54px] md:h-[54px] w-[20px] h-[20px] transition-all duration-150"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <circle cx="27" cy="27" r="27" fill="#222" />
                    <path
                      d="M39 21L24.75 35L15 25.4545"
                      stroke="white"
                      strokeWidth="4"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                )}
              </div>
              <p className="md:text-[20px] text-[6px]">
                By ticking I confirm that I agree with the{" "}
                <a className="underline hover:text-teal-600" href="/TermsAndCondition">
                  Terms and conditions
                </a>
                ,<a href="/DataPolicy underline">Data </a>and{" "}
                <a className="underline hover:text-teal-600" href="/PrivacyPolicy">
                  Privacy policy
                </a>
                .
              </p>
            </div>

            <div className="flex justify-end md:space-x-6 space-x-2 items-center">
              <a href="/Policy">
                <div>
                  {renderButtonWithIcon("POLICIES", "#152C24", "#FDBB26", true)}
                  <p className="md:text-[14px] text-[4px] font-normal mt-1">Click to view policies</p>
                </div>
              </a>
              <div>
                {renderButtonWithIcon("DOWNLOAD REPORTS", "#152C24", "#FDBB26", true)}
                <p className="md:text-[14px] text-[4px] font-normal mt-1">Click to download reports</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Reports;